import { sendGa4Event } from '../../lib/ga4';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useUserContext } from '../../contexts';
import { getCookie, getEnvironment, setCookie } from '../../lib/utils';
import config from '../../lib/config';
import { getProducts, getPlanAddonsFn, getCampaignBundles, getBundleBySlugFn, getBundlesFn, getPurchasedBundlesFn, teamFn, Analytics, meFn } from '../../lib/api';
import productsMetadata from '../../constants/plans';
import planCardOverrides, { DEFAULT_CAMPAIGN_KEY } from '../../constants/planCardOverrides';
import { Spinner } from '../Spinner';
import { getNativeAppClient } from '../NativeAppHandoff';
import { Faq } from '../Faq';
import { Footer } from '../Footer';
import useWindowSize from '../../hooks/useWindowSize';
import { shouldUseCompactPlansLayout } from './plansLayout';
import { shouldUseLegacyProPricing } from './planPricingEligibility';
import featureFlags from '../../lib/featureFlags';
import {
    getBillingPeriodExperimentContext,
    getBillingPeriodExperimentSettings,
    resolveBillingPeriodExperiment,
    clearBillingPeriodExperiment,
} from '../../lib/billingPeriodExperiment';

import './Plans.v3.scss';

const PLAN_IDS = {
    development: {
        free: 67,
        skills_monthly: 79,
        skills_yearly: 80,
        pro_monthly: 74,
        pro2_monthly: 72,
        pro_yearly: 75,
    },
    qa: {
        free: 67,
        skills_monthly: 79,
        skills_yearly: 80,
        pro_monthly: 74,
        pro2_monthly: 72,
        pro_yearly: 75,
    },
    production: {
        free: 1,
        skills_monthly: 63,
        skills_yearly: 65,
        pro_monthly: 30,
        pro2_monthly: 41,
        pro_yearly: 31,
    },
};

const CAMPAIGN_CONFIG = {
    'black-friday': {
        title: 'Black Friday Special',
        subtitle: 'Get 16% Off Your Subscription Plan',
        oldPrice: '$299',
        backgroundVideo: 'https://public.productioncrate.com/videos/black_friday_campaign_bg_v1.mp4',
    },
    'skills-promo': {
        title: 'Skills Tier Special Plan',
        subtitle: 'Gain access to exclusive tutorials',
        oldPrice: '$15/mo',
        backgroundVideo: 'https://public.productioncrate.com/videos/black_friday_campaign_bg_v1.mp4',
    },
    'students-teachers': {
        title: 'Students & Teachers Discount',
        subtitle: 'Save on annual plans',
        oldPrice: '$299/year',
    },
    'plugins-only': {
        title: 'Plugins Only Plan',
        useStandardTemplate: true,
        allowDowngrades: true,
    },
    'plugin-only': {
        title: 'Plugins Only Plan',
        useStandardTemplate: true,
        allowDowngrades: true,
    },
    'cart-subscribe': {
        useTeamLinkCard: true,
    },
};

const normalizePeriod = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (normalized === 'm' || normalized === 'month' || normalized === 'monthly') {
        return 'monthly';
    }
    if (normalized === 'y' || normalized === 'year' || normalized === 'yearly' || normalized === 'annual') {
        return 'yearly';
    }
    return 'yearly';
};

const resolvePeriodLock = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (normalized === 'm' || normalized === 'month' || normalized === 'monthly') {
        return 'monthly';
    }
    if (normalized === 'y' || normalized === 'year' || normalized === 'yearly' || normalized === 'annual') {
        return 'yearly';
    }
    return null;
};

const normalizeProductKey = (name) => {
    const normalized = String(name ?? '').toLowerCase().trim();
    if (normalized === 'sample') return 'skills';
    if (['plugins plan', 'plugins only', 'plugin plan', 'plugin only'].includes(normalized)) return 'plugins plan';
    return normalized;
};

const normalizeToken = (value) => String(value ?? '').toLowerCase().trim().replace(/\s+/g, ' ');
const isFreeEquivalentPlanName = (value) => {
    const normalized = normalizeToken(value);
    return normalized === 'free' || normalized.includes('false pro');
};
const normalizeQueryParam = (value) => {
    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    return normalized || null;
};
const parseBundleIdParam = (value) => {
    const normalizedValue = normalizeQueryParam(value);
    if (!normalizedValue) {
        return null;
    }
    const parsedBundleId = Number(normalizedValue);
    if (!Number.isFinite(parsedBundleId)) {
        return null;
    }
    return parsedBundleId;
};
const isUnauthenticatedError = (error) => {
    const errorStatus = Number(error?.status ?? error?.response?.status);
    if (Number.isFinite(errorStatus) && errorStatus === 401) {
        return true;
    }

    const normalizedMessage = normalizeToken(error?.message ?? error?.error);
    return normalizedMessage === 'unauthenticated.' || normalizedMessage === 'unauthorized';
};
const PLUGINS_PLAN_PRODUCT_KEY = 'plugins plan';
const BUNDLE_LIFETIME_NOTE = 'Lifetime access';
const ALL_PLUGINS_BUNDLE_SLUG = 'all-plugins';
const ALL_PLUGINS_VISIBLE_FEATURES = 5;
const USD_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const formatAddonPrice = (price) => {
    const parsedPrice = Number(price);
    if (!Number.isFinite(parsedPrice)) {
        return '$0.00';
    }
    return USD_PRICE_FORMATTER.format(parsedPrice);
};
const getAddonPriceId = (addon) => {
    const parsedAddonPriceId = Number(addon?.price?.id ?? addon?.addon_price_id ?? addon?.price_id);
    if (!Number.isFinite(parsedAddonPriceId)) {
        return null;
    }
    return parsedAddonPriceId;
};
const getAddonPriceValue = (addon) => {
    const parsedAddonPriceValue = Number(addon?.price?.price ?? addon?.price ?? 0);
    if (!Number.isFinite(parsedAddonPriceValue)) {
        return 0;
    }
    return parsedAddonPriceValue;
};
const getAddonPeriodicityType = (addon) => {
    const periodicityType = addon?.price?.periodicity_type ?? addon?.periodicity_type ?? '';
    return String(periodicityType).trim();
};

const normalizePeriodicityType = (value) => {
    const normalized = normalizeToken(value);
    if (normalized === 'm' || normalized === 'month' || normalized === 'monthly') {
        return 'month';
    }
    if (normalized === 'y' || normalized === 'year' || normalized === 'yearly' || normalized === 'annual') {
        return 'year';
    }
    return normalized;
};

const buildPlanSignature = (plan) => {
    const planName = normalizeToken(plan?.name);
    const periodicityType = normalizePeriodicityType(plan?.periodicity_type);
    if (!planName || !periodicityType) {
        return null;
    }
    return `${planName}|${periodicityType}`;
};

const normalizeOverrideFeatures = (features) => {
    if (!Array.isArray(features)) return null;
    return features
        .map((feature) => {
            if (typeof feature === 'string') {
                const label = feature.trim();
                return label ? { label } : null;
            }
            if (!feature || typeof feature !== 'object') return null;
            if (feature.isDivider || feature.isHeader) return feature;
            const label = String(feature.label ?? '').trim();
            const html = typeof feature.html === 'string' ? feature.html : undefined;
            if (!label && !html) return null;
            return { ...feature, ...(label ? { label } : {}) };
        })
        .filter((feature) => !!feature);
};

const applyPlanCardOverride = (baseCard, override) => {
    if (!override) return baseCard;

    const overrideFeatures = normalizeOverrideFeatures(override.features);
    return {
        ...baseCard,
        ...(override.name !== undefined ? { name: override.name } : {}),
        ...(override.subtitle !== undefined ? { subtitle: override.subtitle } : {}),
        ...(override.downloads !== undefined ? { downloads: override.downloads } : {}),
        ...(override.downloadsRowText !== undefined ? { downloadsRowText: override.downloadsRowText } : {}),
        ...(override.downloadsSubline !== undefined ? { downloadsSubline: override.downloadsSubline } : {}),
        ...(override.accent !== undefined ? { accent: Boolean(override.accent) } : {}),
        ...(override.accentTag !== undefined ? { accentTag: override.accentTag } : {}),
        ...(override.ctaLabel !== undefined ? { ctaLabel: override.ctaLabel } : {}),
        ...(override.ctaDisabled !== undefined ? { ctaDisabled: Boolean(override.ctaDisabled) } : {}),
        ...(override.showDownloadsRow !== undefined ? { showDownloadsRow: Boolean(override.showDownloadsRow) } : {}),
        ...(overrideFeatures ? { features: overrideFeatures } : {}),
    };
};

    const resolveOverrideProductKey = (productName) => {
    const normalizedProductName = normalizeProductKey(productName);
    if (normalizedProductName.includes('skills') || normalizedProductName === 'sample') return 'skills';
    if (normalizedProductName.includes('pro')) return 'pro';
    if (normalizedProductName.includes('enterprise')) return 'enterprise';
    return normalizedProductName;
};

const resolvePlanCardOverride = ({ campaign, productKey, plan, environment }) => {
    if (!productKey) return null;

    const campaignKeys = [campaign ?? DEFAULT_CAMPAIGN_KEY, DEFAULT_CAMPAIGN_KEY]
        .filter((key, index, keys) => !!key && keys.indexOf(key) === index);
    const planIdKey = plan?.id === undefined || plan?.id === null ? null : String(plan.id);
    const planPeriodKey = normalizePeriodicityType(plan?.periodicity_type);
    const planSignature = buildPlanSignature(plan);

    for (const campaignKey of campaignKeys) {
        const productOverrides = planCardOverrides?.[campaignKey]?.[productKey];
        if (!productOverrides) continue;

        const envIdOverrides = productOverrides?.byEnvPlanId?.[environment] ?? {};
        if (planIdKey && envIdOverrides[planIdKey]) {
            return envIdOverrides[planIdKey];
        }

        const planIdOverrides = productOverrides?.byPlanId ?? {};
        if (planIdKey && planIdOverrides[planIdKey]) {
            return planIdOverrides[planIdKey];
        }

        const periodOverrides = productOverrides?.byPeriod ?? {};
        if (planPeriodKey && periodOverrides[planPeriodKey]) {
            return periodOverrides[planPeriodKey];
        }

        const signatureOverrides = productOverrides?.bySignature ?? {};
        if (planSignature && signatureOverrides[planSignature]) {
            return signatureOverrides[planSignature];
        }
    }

    return null;
};

const mapProductsByType = (products) => {
    const mapped = {};

    (products ?? []).forEach((product) => {
        const productKey = normalizeProductKey(product.name);
        const normalizedProductName = String(product?.name ?? '').toLowerCase();
        const plans = Array.isArray(product.plans) ? product.plans : [];
        const localMetadata = productsMetadata.find((metadata) => {
            const metadataName = String(metadata?.name ?? '').toLowerCase();
            if (productKey === 'skills' && metadataName.includes('skills')) return true;
            if (normalizedProductName.includes('pro') && metadataName === 'pro') return true;
            if (normalizedProductName.includes('enterprise') && metadataName === 'enterprise') return true;
            return metadataName === productKey;
        }) ?? null;

        const monthly = plans.find((plan) => String(plan.periodicity_type ?? '').toLowerCase() === 'month' && String(plan.name ?? '').toLowerCase() !== 'free') ?? null;
        const yearly = [...plans].reverse().find((plan) => String(plan.periodicity_type ?? '').toLowerCase() === 'year' && String(plan.name ?? '').toLowerCase() !== 'free') ?? null;
        const free = [...plans].reverse().find((plan) => String(plan.name ?? '').toLowerCase() === 'free') ?? null;

        mapped[productKey] = {
            ...product,
            metadata: localMetadata,
            monthly,
            yearly,
            free,
        };
    });

    return mapped;
};

const getTeamSize = (planName) => {
    const matchedSize = String(planName ?? '').match(/\d+/);
    if (matchedSize) {
        return Number(matchedSize[0]);
    }
    return 5;
};

export const getTeamPlanDisplayName = (teamSize) => `${teamSize} Users`;

export const getTeamPlanMembersLabel = (teamSize) => {
    const teamSizeRanges = {
        5: '1-5',
        10: '6-10',
        20: '11-20',
    };

    return `${teamSizeRanges[teamSize] ?? teamSize} Team Members`;
};

export const TEAM_PLAN_SUBTITLE = '1 admin account';

const toFeatureLabel = (feature) => {
    if (!feature) return '';
    return String(feature.name ?? '').replace(/<[^>]*>/g, '').trim();
};

const toCampaignCardFeatures = (features = []) => {
    return features
        .map((feature) => {
            if (feature?.variable) {
                const value = feature?.donatorValue ?? feature?.freeValue ?? '';
                return { label: `${String(value).replace(/<[^>]*>/g, '').trim()} ${toFeatureLabel(feature)}`.trim() };
            }
            return { label: toFeatureLabel(feature) };
        })
        .filter((feature) => !!feature.label);
};

const toBundleCardFeatures = ({ description, apps } = {}) => {
    const descriptionFeatures = String(description ?? '')
        .split(/\r?\n+/)
        .map((line) => line.replace(/^[\s*-]+/, '').trim())
        .filter((line) => !!line)
        .map((line) => {
            if (line.endsWith(':')) {
                return { label: line, isHeader: true };
            }
            return { label: line };
        });
    const appFeatures = (apps ?? [])
        .map((app) => String(app?.name ?? '').trim())
        .filter((name) => !!name)
        .map((name) => ({ label: name }));

    const seenFeatureKeys = new Set();
    const combinedFeatures = descriptionFeatures.length > 0 ? descriptionFeatures : appFeatures;
    return combinedFeatures.filter((feature) => {
        const featureLabel = String(feature?.label ?? '').trim();
        if (!featureLabel) {
            return false;
        }
        const featureType = feature?.isHeader ? 'header' : 'item';
        const featureKey = `${featureType}:${normalizeToken(featureLabel)}`;
        if (seenFeatureKeys.has(featureKey)) {
            return false;
        }
        seenFeatureKeys.add(featureKey);
        return true;
    });
};

const toBundlePluginNames = (apps = []) => {
    const seenPluginNames = new Set();
    return (apps ?? [])
        .map((app) => String(app?.name ?? '').trim())
        .filter((name) => {
            if (!name) {
                return false;
            }
            const normalizedName = normalizeToken(name);
            if (seenPluginNames.has(normalizedName)) {
                return false;
            }
            seenPluginNames.add(normalizedName);
            return true;
        });
};

const getBundleLicenseTiers = (bundle) => {
    if (!Array.isArray(bundle?.license_tiers)) {
        return [];
    }

    return bundle.license_tiers
        .map((tier) => ({
            ...tier,
            id: Number(tier?.id),
            member_count: Number(tier?.member_count),
            price: Number(tier?.price),
        }))
        .filter((tier) => Number.isFinite(tier.id) && tier.id > 0 && Number.isFinite(tier.member_count) && tier.member_count > 0)
        .sort((firstTier, secondTier) => firstTier.member_count - secondTier.member_count || firstTier.id - secondTier.id);
};

const getTierLicenseLabel = (memberCount) => `${memberCount} ${memberCount === 1 ? 'Member' : 'Members'} License`;

const toCollection = (payload) => Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
const toNonNegativeNumber = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : fallback;
};
const getTeamBundleId = (teamBundle) => teamBundle?.id ?? teamBundle?.team_bundle_id ?? null;
const getTeamBundleBundleId = (teamBundle) => teamBundle?.bundle?.id ?? teamBundle?.bundle_id ?? null;
const getTeamBundleCurrentTierId = (teamBundle) => teamBundle?.bundle_license_tier_id ?? teamBundle?.license_tier_id ?? teamBundle?.license_tier?.id ?? null;
const getCheckoutUrl = (payload) => payload?.checkout_url || payload?.url || payload?.data?.checkout_url || payload?.data?.url;

const getBundleAppPrimaryToken = (app) => {
    if (app === null || app === undefined) {
        return null;
    }
    const possibleValues = [
        app?.slug,
        app?.app_slug,
        app?.appSlug,
        app?.name,
        app?.title,
        app?.app_name,
        typeof app === 'string' || typeof app === 'number' ? app : null,
    ];
    const resolvedValue = possibleValues.find((value) => value !== null && value !== undefined && String(value).trim());
    if (resolvedValue === null || resolvedValue === undefined) {
        return null;
    }
    const normalizedValue = normalizeToken(resolvedValue);
    return normalizedValue || null;
};

const getBundleAppMatchTokens = (app) => {
    if (app === null || app === undefined) {
        return [];
    }

    const possibleValues = [
        app?.slug,
        app?.app_slug,
        app?.appSlug,
        app?.name,
        app?.title,
        app?.app_name,
        typeof app === 'string' || typeof app === 'number' ? app : null,
    ];

    return possibleValues
        .map((value) => (value === null || value === undefined ? '' : normalizeToken(value)))
        .filter((value, index, source) => !!value && source.indexOf(value) === index);
};

const bundleContainsAppSlug = (bundle, normalizedAppSlug) => {
    if (!normalizedAppSlug) {
        return false;
    }

    return (Array.isArray(bundle?.apps) ? bundle.apps : [])
        .some((app) => getBundleAppMatchTokens(app).includes(normalizedAppSlug));
};

const isSingleAppBundleMatch = (bundle, normalizedAppSlug) => {
    if (!normalizedAppSlug || !bundleContainsAppSlug(bundle, normalizedAppSlug)) {
        return false;
    }

    const uniqueBundleAppTokens = (Array.isArray(bundle?.apps) ? bundle.apps : [])
        .map((app) => getBundleAppPrimaryToken(app))
        .filter((token, index, source) => !!token && source.indexOf(token) === index);

    return uniqueBundleAppTokens.length === 1;
};

const filterBundlesByAppSlugPriority = ({ bundles, appSlug } = {}) => {
    const normalizedAppSlug = normalizeToken(appSlug);
    if (!normalizedAppSlug) {
        return [];
    }

    const availableBundles = Array.isArray(bundles) ? bundles : [];
    const matchingBundleSlugs = availableBundles
        .filter((bundle) => normalizeToken(bundle?.slug) === normalizedAppSlug);

    if (matchingBundleSlugs.length > 0) {
        return matchingBundleSlugs;
    }

    const matchingBundles = availableBundles
        .filter((bundle) => bundleContainsAppSlug(bundle, normalizedAppSlug));

    if (matchingBundles.length === 0) {
        return [];
    }

    const singleAppMatchingBundles = matchingBundles
        .filter((bundle) => isSingleAppBundleMatch(bundle, normalizedAppSlug));

    return singleAppMatchingBundles.length > 0 ? singleAppMatchingBundles : matchingBundles;
};

const resolveBundleRecencyTimestamp = (bundle) => {
    const candidateDateValues = [
        bundle?.created_at,
        bundle?.createdAt,
        bundle?.updated_at,
        bundle?.updatedAt,
        bundle?.published_at,
        bundle?.publishedAt,
    ];

    for (const dateValue of candidateDateValues) {
        if (!dateValue) {
            continue;
        }
        const parsedTimestamp = new Date(dateValue).getTime();
        if (Number.isFinite(parsedTimestamp) && parsedTimestamp > 0) {
            return parsedTimestamp;
        }
    }

    return null;
};

const selectNewestBundle = (bundles = []) => {
    if (!Array.isArray(bundles) || bundles.length === 0) {
        return null;
    }

    return [...bundles].sort((bundleA, bundleB) => {
        const timestampA = resolveBundleRecencyTimestamp(bundleA);
        const timestampB = resolveBundleRecencyTimestamp(bundleB);

        if (timestampA !== null && timestampB !== null && timestampA !== timestampB) {
            return timestampB - timestampA;
        }
        if (timestampA !== null && timestampB === null) {
            return -1;
        }
        if (timestampA === null && timestampB !== null) {
            return 1;
        }

        const idA = Number(bundleA?.id);
        const idB = Number(bundleB?.id);
        if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) {
            return idB - idA;
        }

        return 0;
    })[0] ?? null;
};

const selectPreferredBundleByAppSlug = ({ bundles, appSlug } = {}) => {
    const prioritizedMatches = filterBundlesByAppSlugPriority({ bundles, appSlug });
    if (!Array.isArray(prioritizedMatches) || prioritizedMatches.length === 0) {
        return null;
    }
    return selectNewestBundle(prioritizedMatches);
};

const tryParseJsonString = (value) => {
    if (typeof value !== 'string') {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const toMetadataValueString = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        return trimmedValue || null;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        const joinedValue = value
            .map((entry) => toMetadataValueString(entry))
            .filter((entry) => !!entry)
            .join(', ');
        return joinedValue || null;
    }
    if (typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'url')) {
            return toMetadataValueString(value.url);
        }
        if (Object.prototype.hasOwnProperty.call(value, 'src')) {
            return toMetadataValueString(value.src);
        }
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
            return toMetadataValueString(value.value);
        }
        if (Object.prototype.hasOwnProperty.call(value, 'content')) {
            return toMetadataValueString(value.content);
        }
        if (Object.prototype.hasOwnProperty.call(value, 'data')) {
            return toMetadataValueString(value.data);
        }
    }
    return null;
};

const getMetadataEntries = (source) => {
    if (!source) {
        return [];
    }
    if (Array.isArray(source)) {
        return source
            .map((entry) => {
                if (!entry || typeof entry !== 'object') {
                    return null;
                }
                const metadataKey = entry.key ?? entry.name ?? entry.slug ?? entry.id;
                if (metadataKey === null || metadataKey === undefined) {
                    return null;
                }
                const metadataValue = entry.value ?? entry.content ?? entry.data ?? entry.meta_value ?? entry;
                return [String(metadataKey), metadataValue];
            })
            .filter((entry) => Array.isArray(entry));
    }
    if (typeof source === 'object') {
        return Object.entries(source);
    }
    return [];
};

const getBundleMetadataValue = (bundle, key) => {
    const normalizedKey = normalizeToken(key);
    const rawMetadata = bundle?.metadata;
    const parsedMetadata = typeof rawMetadata === 'string'
        ? tryParseJsonString(rawMetadata)
        : rawMetadata;

    const metadataSources = [parsedMetadata].filter((source) => !!source);

    if (parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)) {
        ['metadata', 'data', 'attributes', 'meta', 'fields', 'items'].forEach((nestedSourceKey) => {
            const nestedSourceValue = parsedMetadata[nestedSourceKey];
            const parsedNestedSource = typeof nestedSourceValue === 'string'
                ? tryParseJsonString(nestedSourceValue)
                : nestedSourceValue;
            if (parsedNestedSource) {
                metadataSources.push(parsedNestedSource);
            }
        });
    }

    for (const metadataSource of metadataSources) {
        const metadataEntries = getMetadataEntries(metadataSource);
        for (const [metadataKey, metadataValue] of metadataEntries) {
            if (normalizeToken(metadataKey) !== normalizedKey) {
                continue;
            }
            const resolvedValue = toMetadataValueString(metadataValue);
            if (resolvedValue) {
                return resolvedValue;
            }
        }
    }

    return null;
};

const toBundleCardImageUrl = (bundle) => {
    const directImageUrl = toMetadataValueString(bundle?.bundle_card_img ?? bundle?.bundleCardImg ?? bundle?.bundle_card_image);
    if (directImageUrl) {
        return directImageUrl;
    }
    return getBundleMetadataValue(bundle, 'bundle_card_img');
};

const toBundleRedirectUrl = (bundle) => {
    const directRedirectUrl = toMetadataValueString(bundle?.redirect_url ?? bundle?.redirectUrl);
    if (directRedirectUrl) {
        return directRedirectUrl;
    }
    return getBundleMetadataValue(bundle, 'redirect_url');
};

const inferDownloadsFromMetadata = (metadata, fallback) => {
    const variableDownloads = (metadata?.features ?? []).find((feature) => feature?.variable && String(feature?.name ?? '').toLowerCase().includes('downloads'));
    if (variableDownloads?.donatorValue) {
        return `${String(variableDownloads.donatorValue).replace(/<[^>]*>/g, '').replace('*', '').trim()} Basic`;
    }
    return fallback;
};

const getEnterpriseDownloads = (features) => {
    const downloadsFeature = (features ?? []).find((feature) => String(feature.name ?? '').toLowerCase().includes('downloads'));
    if (!downloadsFeature) return '100 Pro';

    const match = String(downloadsFeature.name).replace(/,/g, '').match(/(\d+)/);
    if (!match) return '100 Pro';

    return `${match[1]} Pro`;
};

const isTeamMembersFeature = (feature) => {
    const featureLabel = normalizeToken(toFeatureLabel(feature));
    return featureLabel.includes('team') && featureLabel.includes('member');
};

const mapEnterprisePlans = (products) => {
    const enterprisePlans = (products ?? [])
        .filter((product) => String(product.name ?? '').toLowerCase() === 'enterprise')
        .flatMap((product) => product.plans ?? [])
        .map((plan) => {
            const teamSize = getTeamSize(plan.name);
            const metadata = productsMetadata.find((meta) => String(meta.name ?? '').toLowerCase() === `enterprise plus ${teamSize}`);
            return {
                ...plan,
                teamSize,
                metadata,
            };
        })
        .filter((plan) => !!plan.metadata)
        .sort((a, b) => {
            if (a.teamSize !== b.teamSize) return a.teamSize - b.teamSize;
            return normalizePeriod(a.periodicity_type) === 'yearly' ? -1 : 1;
        });

    return enterprisePlans;
};

const formatMonthlyEquivalent = (yearlyPrice) => {
    return Math.ceil(Number(yearlyPrice) / 12);
};

const getMonthlyBillingNote = (monthlyPlan, yearlyPlan) => {
    if (!monthlyPlan || !yearlyPlan) return null;

    const yearlySavings = Math.round(Number(monthlyPlan.price) * 12 - Number(yearlyPlan.price));
    if (!Number.isFinite(yearlySavings) || yearlySavings <= 0) return null;

    return {
        prefix: 'or ',
        linkText: `$${formatMonthlyEquivalent(yearlyPlan.price)}/mo billed yearly`,
        suffix: ` — save $${yearlySavings}`,
    };
};

const Tabs = ({ onChange, value, yearlySavings }) => (
    <div className={`plans_v3__tabs ${value === 'monthly' ? 'right' : ''} ${yearlySavings > 0 ? 'has-savings' : ''}`}>
        <div
            className={`plans_v3__tabs__button ${value === 'yearly' ? 'active' : ''}`}
            onClick={() => onChange('yearly')}
        >
            Yearly
            {yearlySavings > 0 && <span className="plans_v3__tabs__savings">SAVE ${yearlySavings}</span>}
        </div>
        <div
            className={`plans_v3__tabs__button ${value === 'monthly' ? 'active' : ''}`}
            onClick={() => onChange('monthly')}
        >
            Monthly
        </div>
        <div className="plans_v3__tabs__pill" />
    </div>
);

const Card = ({ plan, onClick, onBillingNoteClick }) => {
    const hasDownloadsTitle = plan.showDownloadsRow !== undefined
        ? Boolean(plan.showDownloadsRow)
        : !!plan.downloads;
    const showOldPrice = plan.oldPrice !== undefined && plan.oldPrice !== null;
    const showNumericPrice = plan.price !== undefined && plan.price !== null;
    const resolvedPricePeriod = plan.pricePeriod === undefined ? '/mo' : plan.pricePeriod;
    const showPricePeriod = resolvedPricePeriod !== null && resolvedPricePeriod !== '';
    const subtitleMarginClass = plan.subtitleMarginClass || 'm-b-24';
    const priceBlockMarginClass = plan.priceBlockMarginClass || (!plan.billingNote ? 'm-b-32' : '');
    const priceRightTextLines = Array.isArray(plan.priceRightTextLines)
        ? plan.priceRightTextLines
            .map((line) => String(line ?? '').trim())
            .filter((line) => !!line)
        : [];
    const shouldUseSmallDecimalPrice = Boolean(plan.priceWithSmallDecimals);
    const parsedPrice = Number(plan.price);
    const hasValidNumericPrice = Number.isFinite(parsedPrice);
    const normalizedPriceText = hasValidNumericPrice
        ? (Number.isInteger(parsedPrice) ? String(Math.trunc(parsedPrice)) : parsedPrice.toFixed(2))
        : String(plan.price ?? '');
    const [priceIntegerPart = normalizedPriceText, priceDecimalPart = ''] = normalizedPriceText.split('.');
    const showReducedDecimal = shouldUseSmallDecimalPrice && !!priceDecimalPart;
    const cardExtraClasses = [plan.campaignClass, plan.cardClass].filter(Boolean).join(' ');
    const bundleImageUrl = typeof plan.bundleImageUrl === 'string' ? plan.bundleImageUrl.trim() : '';
    const bundleMetadataRows = Array.isArray(plan.bundleMetadataRows)
        ? plan.bundleMetadataRows.filter((row) => {
            const hasLabel = String(row?.label ?? '').trim().length > 0;
            const hasValue = String(row?.value ?? '').trim().length > 0;
            return hasLabel && hasValue;
        })
        : [];
    const downloadsSubline = plan.downloadsSubline && String(plan.downloadsSubline?.label ?? '').trim()
        ? {
            label: String(plan.downloadsSubline.label).trim(),
            iconSrc: typeof plan.downloadsSubline.iconSrc === 'string' ? plan.downloadsSubline.iconSrc.trim() : '',
            iconAlt: typeof plan.downloadsSubline.iconAlt === 'string' ? plan.downloadsSubline.iconAlt.trim() : '',
        }
        : null;
    const licenseTiers = Array.isArray(plan.licenseTiers) ? plan.licenseTiers : [];
    const bundleTierSummary = plan.bundleTierSummary;

    return (
        <div className={`plans_v3__plan_card ${plan.accent ? 'accent' : ''} ${cardExtraClasses}`}>
            {plan.accentTag && <div className="plans_v3__plan_card__accent_tag">{plan.accentTag}</div>}
            <div className="plans_v3__plan_card__title">{plan.name}</div>
            {plan.subtitle && <div className={`plans_v3__plan_card__subtitle ${subtitleMarginClass}`}>{plan.subtitle}</div>}

            {plan.customPriceIcon && (
                <div className="plans_v3__plan_card__custom_price_icon" aria-hidden="true">
                    <img src={plan.customPriceIcon} alt="" />
                </div>
            )}

            {showOldPrice && <div className={`plans_v3__plan_card__old_price ${plan.oldPriceNoStrike ? 'no_strike' : ''}`}>{plan.oldPrice}</div>}

            {showNumericPrice && (
                <div className={`plans_v3__plan_card__price_block ${priceBlockMarginClass}`}>
                    <div className="plans_v3__plan_card__price_block__symbol">$</div>
                    <div className="plans_v3__plan_card__price_block__price">
                        {showReducedDecimal ? (
                            <>
                                <span className="plans_v3__plan_card__price_block__price__integer">{priceIntegerPart}</span>
                                <span className="plans_v3__plan_card__price_block__price__decimal">.{priceDecimalPart}</span>
                            </>
                        ) : (
                            plan.price
                        )}
                    </div>
                    {showPricePeriod && <div className="plans_v3__plan_card__price_block__period">{resolvedPricePeriod}</div>}
                    {priceRightTextLines.length > 0 && (
                        <div className="plans_v3__plan_card__price_block__right_text">
                            {priceRightTextLines.map((line, index) => (
                                <span key={`${plan.name}-price-right-text-${index}`}>{line}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!!plan.priceText && <div className="plans_v3__plan_card__custom_price">{plan.priceText}</div>}

            {plan.pic && !plan.price && (
                <img className="plans_v3__plan_card__pic m-b-12" src={plan.pic} alt="team icon" />
            )}

            {plan.billingNote && (
                <div className="plans_v3__plan_card__price_note m-b-12">
                    {typeof plan.billingNote === 'string' ? plan.billingNote : (
                        <>
                            {plan.billingNote.prefix}
                            <button
                                type="button"
                                className="plans_v3__plan_card__price_note__yearly_link"
                                onClick={() => onBillingNoteClick?.()}
                            >
                                {plan.billingNote.linkText}
                            </button>
                            {plan.billingNote.suffix}
                        </>
                    )}
                </div>
            )}

            {plan.showSavingsTagSlot && (
                <div className={`plans_v3__plan_card__savings_slot ${plan.savingsTagSlotClassName || ''}`}>
                    {plan.savingsTag && <span className="plans_v3__plan_card__savings_tag">{plan.savingsTag}</span>}
                </div>
            )}

            <div
                className={`plans_v3__plan_card__cta ${plan.ctaDisabled ? 'disabled' : ''}`}
                onClick={() => {
                    if (!plan.ctaDisabled) onClick();
                }}
            >
                <div className="plans_v3__plan_card__cta__content">
                    {plan.ctaLeadingIcon === 'cart' && !plan.ctaDisabled && (
                        <img
                            className={`plans_v3__plan_card__cta__icon ${plan.ctaLeadingIconClassName ?? ''}`}
                            src="/images/cart_checkout.svg"
                            alt=""
                            aria-hidden="true"
                        />
                    )}
                    <span>{plan.ctaLabel}</span>
                </div>
            </div>

            {licenseTiers.length > 1 && (
                <label className="plans_v3__plan_card__tier_selector">
                    <span>Members</span>
                    <select
                        value={plan.selectedBundleLicenseTierId ?? ''}
                        onChange={(event) => plan.onBundleLicenseTierChange?.(Number(event.target.value))}
                        aria-label={`${plan.name} member count`}
                    >
                        {licenseTiers.map((tier) => (
                            <option key={`${plan.name}-tier-${tier.id}`} value={tier.id}>
                                {getTierLicenseLabel(tier.member_count)}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {bundleTierSummary && (
                <div className={`plans_v3__plan_card__tier_summary ${bundleTierSummary.isUpgrade ? 'is_upgrade' : ''}`}>
                    <span>Current license: {bundleTierSummary.currentLicenseLabel} ({bundleTierSummary.currentTierPrice})</span>
                    {bundleTierSummary.isUpgrade && (
                        <>
                            <span>Selected tier total: {bundleTierSummary.selectedTierPrice}</span>
                            <strong>Due today: {bundleTierSummary.dueToday}</strong>
                            <small>Final amount is calculated securely at checkout.</small>
                        </>
                    )}
                    {bundleTierSummary.error && <strong className="is_error">{bundleTierSummary.error}</strong>}
                </div>
            )}

            {hasDownloadsTitle && (
                <>
                    <div className="plans_v3__plan_card__features_title">
                        {plan.downloadsRowText ?? <><b>{plan.downloads}</b> Downloads Per Day</>}
                    </div>
                    {downloadsSubline && (
                        <div className="plans_v3__plan_card__downloads_subline">
                            {downloadsSubline.iconSrc && (
                                <img
                                    src={downloadsSubline.iconSrc}
                                    alt={downloadsSubline.iconAlt}
                                    className="plans_v3__plan_card__downloads_subline__icon"
                                />
                            )}
                            <span>{downloadsSubline.label}</span>
                        </div>
                    )}
                    <div className="plans_v3__plan_card__features_list__divider" />
                </>
            )}

            <div className="plans_v3__plan_card__features_list">
                {(plan.features ?? []).map((feature, index) => (
                    <React.Fragment key={`${plan.name}-${index}`}>
                        {feature.isDivider && <div className="plans_v3__plan_card__features_list__divider" />}
                        {feature.isHeader && <div className="plans_v3__plan_card__features_list__header m-b-8">{feature.label}</div>}
                        {!feature.isDivider && !feature.isHeader && (
                            <div
                                className={`plans_v3__plan_card__features_list__item ${feature.disabled ? 'disabled' : ''} ${feature.noIcon ? 'no_icon' : ''} ${feature.tooltip ? 'has_tooltip' : ''} ${feature.emphasized ? 'emphasized' : ''} ${feature.marginBottom ? 'm-b-10' : ''} ${feature.marginBottom12 ? 'm-b-12' : ''}`}
                                tabIndex={feature.tooltip ? 0 : undefined}
                            >
                                {!feature.noIcon && <img src="/images/icons.plans.check.orange.svg" alt="checkmark" className={`plans_v3__plan_card__features_list__item__icon ${feature.disabled ? 'disabled' : ''}`} />}
                                <div className="plans_v3__plan_card__features_list__item__label">
                                    {feature.html
                                        ? <span dangerouslySetInnerHTML={{ __html: feature.html }} />
                                        : feature.label}
                                </div>
                                {feature.tooltip && (
                                    <div className="plans_v3__plan_card__features_list__item__tooltip" role="tooltip">
                                        {feature.tooltip}
                                    </div>
                                )}
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            {bundleImageUrl && (
                <img
                    className="plans_v3__plan_card__bundle_image m-t-16 m-b-12"
                    src={bundleImageUrl}
                    alt={`${plan.name} bundle preview`}
                />
            )}

            {bundleMetadataRows.length > 0 && (
                <div className="plans_v3__plan_card__bundle_metadata">
                    {bundleMetadataRows.map((row, index) => (
                        <div className="plans_v3__plan_card__bundle_metadata__row" key={`${plan.name}-bundle-metadata-${index}`}>
                            <span className="plans_v3__plan_card__bundle_metadata__label">{row.label}:</span>
                            <span className="plans_v3__plan_card__bundle_metadata__value">{row.value}</span>
                        </div>
                    ))}
                </div>
            )}

            {plan.note && <div className="plans_v3__plan_card__note">{plan.note}</div>}
        </div>
    );
};

const PlansV3 = ({ upgrade = false, user: userProp, forceFree = false, enterpriseOnly = false }) => {
    const { campaign: campaignParam } = useParams();
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const nativeApp = getNativeAppClient(query);
    const queryCampaign = useMemo(() => normalizeQueryParam(query.get('campaign')), [query]);
    const { width: viewportWidth } = useWindowSize();
    const requestedCampaign = campaignParam ?? queryCampaign;
    const normalizedCampaignParam = normalizeToken(requestedCampaign ?? '');
    const isPluginsOnlySlugFromParam = normalizedCampaignParam === 'plugins-only';
    const paymentLinkId = useMemo(() => normalizeQueryParam(query.get('payment_link_id')), [query]);
    const requestedBundleId = useMemo(
        () => parseBundleIdParam(query.get('bundle_id') ?? query.get('bundleId')),
        [query]
    );
    const requestedAppSlug = useMemo(
        () => normalizeQueryParam(query.get('app_slug') ?? query.get('appSlug')),
        [query]
    );
    const isAllPluginsBundle = normalizeToken(requestedAppSlug) === ALL_PLUGINS_BUNDLE_SLUG;

    const { user: contextUser, isAuthenticating, setUser, reloadCurrentUser } = useUserContext();
    const user = userProp ?? contextUser;
    const currentPlan = user?.team?.subscription?.plan ?? null;
    const currentPlanId = currentPlan?.id ?? null;
    const currentSubscriptionPrice = Number(currentPlan?.price ?? 0);
    const isCurrentPriceValid = Number.isFinite(currentSubscriptionPrice);
    const currentSubscriptionPriceSafe = isCurrentPriceValid ? currentSubscriptionPrice : 0;
    const isFreeCurrentPlan = !!currentPlan && (
        isFreeEquivalentPlanName(currentPlan?.name) ||
        currentSubscriptionPriceSafe === 0
    );
    const hasPaidSubscription = !!currentPlan && !isFreeCurrentPlan;
    const hasActiveRealSubscription = hasPaidSubscription || !!(user?.team?.subscription?.stripe || user?.team?.subscription?.paypal);
    const lockedBillingPeriod = hasPaidSubscription ? resolvePeriodLock(currentPlan?.periodicity_type) : null;

    const useLegacyProPricing = shouldUseLegacyProPricing({
        user,
        registrationCookie: getCookie('crateRegistration'),
    });

    const [paymentProvider, setPaymentProvider] = useState(query.get('prov') === 'p' ? 'p' : 's');
    const [billingPeriod, setBillingPeriod] = useState(() => {
        const queryPeriod = normalizePeriod(query.get('freq'));
        if (query.get('freq')) return queryPeriod;
        if (isPluginsOnlySlugFromParam) return 'monthly';
        return 'yearly';
    });

    const [isLoadingPlans, setIsLoadingPlans] = useState(false);
    const [productsByType, setProductsByType] = useState(null);
    const [campaignBundles, setCampaignBundles] = useState([]);
    const [selectedBundleTierIds, setSelectedBundleTierIds] = useState({});
    const [purchasedBundleIds, setPurchasedBundleIds] = useState([]);
    const [teamBundlePurchases, setTeamBundlePurchases] = useState([]);
    const [bundlePurchaseRefreshKey, setBundlePurchaseRefreshKey] = useState(0);
    const [upgradingTeamBundleIds, setUpgradingTeamBundleIds] = useState({});
    const [bundleUpgradeErrors, setBundleUpgradeErrors] = useState({});
    const [isLoadingBundles, setIsLoadingBundles] = useState(false);
    const [enterprisePlans, setEnterprisePlans] = useState([]);
    const [isAddonUpsellLoading, setIsAddonUpsellLoading] = useState(false);
    const [isAddonUpsellVisible, setIsAddonUpsellVisible] = useState(false);
    const [addonUpsellOptions, setAddonUpsellOptions] = useState([]);
    const [addonUpsellSelectedIds, setAddonUpsellSelectedIds] = useState([]);
    const [addonUpsellContext, setAddonUpsellContext] = useState(null);
    const [addonUpsellError, setAddonUpsellError] = useState(null);
    const [enterprisePlanAddonsById, setEnterprisePlanAddonsById] = useState({});
    const [billingPeriodExperiment, setBillingPeriodExperiment] = useState(null);

    const freeRedirectTriggeredRef = useRef(false);
    const ga4PricingTrackedRef = useRef(false);
    const hasUserSelectedBillingRef = useRef(false);
    const addonUpsellCacheRef = useRef({});
    const billingPeriodExperimentExposureRef = useRef(null);
    const billingPeriodExperimentSettingsSyncRef = useRef(null);
    const billingPeriodExperimentExclusionRef = useRef(null);

    const activeCampaign = enterpriseOnly ? null : requestedCampaign ?? null;
    const normalizedCampaignSlug = normalizeToken(activeCampaign ?? '');
    const isPluginsOnlySlugCampaign = normalizedCampaignSlug === 'plugins-only';
    const campaignConfig = activeCampaign
        ? (CAMPAIGN_CONFIG[activeCampaign] ?? CAMPAIGN_CONFIG[normalizedCampaignSlug])
        : null;
    const useTeamLinkCard = Boolean(campaignConfig?.useTeamLinkCard);
    const hasPluginsOnlyCampaignProduct = useMemo(() => {
        if (!activeCampaign || !productsByType) return false;
        return !!productsByType[PLUGINS_PLAN_PRODUCT_KEY];
    }, [activeCampaign, productsByType]);
    const isPluginsOnlyCampaign = Boolean(
        activeCampaign && (normalizedCampaignSlug.includes('plugin') || hasPluginsOnlyCampaignProduct)
    );
    const forceStandardTemplateForCampaign = Boolean(activeCampaign && (campaignConfig?.useStandardTemplate || isPluginsOnlyCampaign));
    const allowDowngradesInCampaign = Boolean(activeCampaign && (campaignConfig?.allowDowngrades || isPluginsOnlyCampaign));
    const useRegisterStylePricingForCampaign = Boolean(forceStandardTemplateForCampaign && isPluginsOnlyCampaign);
    const runtimeEnvironment = useMemo(() => getEnvironment(), []);
    const resolvedCampaignKey = activeCampaign ?? DEFAULT_CAMPAIGN_KEY;
    const normalizedPathname = String(location.pathname ?? '').replace(/\/+$/, '') || '/';
    const experimentSurface = normalizedPathname === '/register'
        ? (user ? 'register_free' : 'register_anonymous')
        : ((normalizedPathname === '/upgrade' || normalizedPathname.startsWith('/upgrade/')) && user ? 'upgrade_free' : null);
    const hasBillingPeriodExperimentRouteExclusion = Boolean(
        activeCampaign
        || paymentLinkId
        || requestedBundleId
        || requestedAppSlug
        || query.get('freq')
        || enterpriseOnly
    );

    useEffect(() => {
        try {
            Analytics.view('page', { target: window.location.href });
        } catch (error) {
            console.log('Error while sending page view', error);
        }
    }, []);

    useEffect(() => {
        if (user?.team?.subscription?.paypal || user?.team?.subscription?.plan?.paypal) {
            setPaymentProvider('p');
        }
    }, [user?.team?.subscription?.paypal, user?.team?.subscription?.plan?.paypal]);

    useEffect(() => {
        const shouldFetchProducts = enterpriseOnly || !!activeCampaign || !!paymentLinkId;
        if (!shouldFetchProducts) {
            setProductsByType(null);
            setEnterprisePlanAddonsById({});
            return;
        }

        setIsLoadingPlans(true);

        const campaign = enterpriseOnly ? 'enterprise-only' : activeCampaign;
        getProducts({ campaign, paymentLinkId })
            .then((products) => {
                if (enterpriseOnly) {
                    setEnterprisePlans(mapEnterprisePlans(products));
                    return;
                }
                setProductsByType(mapProductsByType(products));
            })
            .catch((error) => {
                console.log('Error while loading plans data', error);
                if (enterpriseOnly) {
                    setEnterprisePlans([]);
                    setEnterprisePlanAddonsById({});
                } else {
                    setProductsByType({});
                }
            })
            .finally(() => {
                setIsLoadingPlans(false);
            });
    }, [activeCampaign, enterpriseOnly, paymentLinkId]);

    useEffect(() => {
        if (enterpriseOnly) {
            setCampaignBundles([]);
            setIsLoadingBundles(false);
            return;
        }

        let isCancelled = false;
        setIsLoadingBundles(true);

        const loadBundles = async () => {
            try {
                if (requestedAppSlug) {
                    try {
                        const bundleBySlug = await getBundleBySlugFn(requestedAppSlug);
                        if (isCancelled) {
                            return;
                        }

                        if (bundleBySlug) {
                            setCampaignBundles([bundleBySlug]);
                            return;
                        }
                    } catch {}
                }

                if (Number.isFinite(requestedBundleId) || requestedAppSlug) {
                    let availableBundles = [];
                    try {
                        availableBundles = await getBundlesFn();
                    } catch (error) {
                        if (!isUnauthenticatedError(error)) {
                            throw error;
                        }
                    }

                    if (isCancelled) {
                        return;
                    }

                    const resolvedBundles = Array.isArray(availableBundles) ? availableBundles : [];
                    if (Number.isFinite(requestedBundleId)) {
                        const selectedBundle = resolvedBundles.find((bundle) => Number(bundle?.id) === requestedBundleId);

                        if (selectedBundle) {
                            setCampaignBundles([selectedBundle]);
                            return;
                        }

                        console.log('Requested bundle id was not found in bundles endpoint', requestedBundleId);
                    }

                    if (requestedAppSlug) {
                        const preferredBundle = selectPreferredBundleByAppSlug({
                            bundles: resolvedBundles,
                            appSlug: requestedAppSlug,
                        });

                        if (preferredBundle) {
                            setCampaignBundles([preferredBundle]);
                            return;
                        }

                        console.log('Requested app slug was not found in bundles endpoint', requestedAppSlug);
                    }
                }

                const bundles = await getCampaignBundles({ campaign: activeCampaign });
                if (isCancelled) {
                    return;
                }
                const resolvedCampaignBundles = Array.isArray(bundles) ? bundles : [];

                if (Number.isFinite(requestedBundleId)) {
                    const selectedBundle = resolvedCampaignBundles.find((bundle) => Number(bundle?.id) === requestedBundleId);
                    if (selectedBundle) {
                        setCampaignBundles([selectedBundle]);
                        return;
                    }
                }

                if (requestedAppSlug) {
                    const preferredBundle = selectPreferredBundleByAppSlug({
                        bundles: resolvedCampaignBundles,
                        appSlug: requestedAppSlug,
                    });
                    if (preferredBundle) {
                        setCampaignBundles([preferredBundle]);
                        return;
                    }
                }

                setCampaignBundles(resolvedCampaignBundles);
            } catch (error) {
                console.log('Error while loading campaign bundles', error);
                if (isCancelled) {
                    return;
                }
                setCampaignBundles([]);
            } finally {
                if (isCancelled) {
                    return;
                }
                setIsLoadingBundles(false);
            }
        };

        loadBundles();

        return () => {
            isCancelled = true;
        };
    }, [activeCampaign, enterpriseOnly, requestedAppSlug, requestedBundleId]);

    useEffect(() => {
        if (enterpriseOnly || !user) {
            setPurchasedBundleIds([]);
            setTeamBundlePurchases([]);
            return;
        }

        let isCancelled = false;

        Promise.allSettled([getBundlesFn(), getPurchasedBundlesFn(), teamFn.appLicenses.list()])
            .then(([bundlesResult, purchasedResult, teamBundlesResult]) => {
                if (isCancelled) {
                    return;
                }

                const activeBundles = bundlesResult.status === 'fulfilled' && Array.isArray(bundlesResult.value)
                    ? bundlesResult.value
                    : [];
                const purchasedBundles = purchasedResult.status === 'fulfilled' && Array.isArray(purchasedResult.value)
                    ? purchasedResult.value
                    : [];
                const nextTeamBundlePurchases = teamBundlesResult.status === 'fulfilled'
                    ? toCollection(teamBundlesResult.value)
                    : [];

                const activeBundleIdsSet = new Set();
                const activeBundleIdByName = new Map();
                const purchasedIdsFromBundles = activeBundles
                    .map((bundle) => {
                        const bundleId = Number(bundle?.id);
                        if (!Number.isFinite(bundleId)) {
                            return null;
                        }
                        activeBundleIdsSet.add(bundleId);
                        const bundleNameKey = normalizeToken(bundle?.name);
                        if (bundleNameKey) {
                            activeBundleIdByName.set(bundleNameKey, bundleId);
                        }
                        return bundle?.is_purchased ? bundleId : null;
                    })
                    .filter((bundleId) => Number.isFinite(bundleId));

                const purchasedIdsFromEntries = purchasedBundles
                    .map((entry) => {
                        const nestedBundleId = Number(entry?.bundle?.id);
                        if (Number.isFinite(nestedBundleId)) {
                            return nestedBundleId;
                        }

                        const directBundleId = Number(entry?.bundle_id);
                        if (Number.isFinite(directBundleId)) {
                            return directBundleId;
                        }

                        const directEntryId = Number(entry?.id);
                        if (Number.isFinite(directEntryId) && activeBundleIdsSet.has(directEntryId)) {
                            return directEntryId;
                        }

                        const bundleNameKey = normalizeToken(entry?.bundle?.name);
                        if (bundleNameKey && activeBundleIdByName.has(bundleNameKey)) {
                            return activeBundleIdByName.get(bundleNameKey);
                        }

                        return null;
                    })
                    .filter((bundleId) => Number.isFinite(bundleId));

                const purchasedIds = [...purchasedIdsFromBundles, ...purchasedIdsFromEntries]
                    .filter((bundleId, index, source) => source.indexOf(bundleId) === index);

                const purchasedIdsFromTeamBundles = nextTeamBundlePurchases
                    .map((teamBundle) => Number(getTeamBundleBundleId(teamBundle)))
                    .filter((bundleId) => Number.isFinite(bundleId));
                setPurchasedBundleIds([...purchasedIds, ...purchasedIdsFromTeamBundles]
                    .filter((bundleId, index, source) => source.indexOf(bundleId) === index));
                setTeamBundlePurchases(nextTeamBundlePurchases);
            })
            .catch((error) => {
                console.log('Error while loading purchased bundles', error);
                if (isCancelled) {
                    return;
                }
                setPurchasedBundleIds([]);
                setTeamBundlePurchases([]);
            });

        return () => {
            isCancelled = true;
        };
    }, [bundlePurchaseRefreshKey, enterpriseOnly, user]);

    const getDefaultPlanData = (planType, period) => {
        const environment = PLAN_IDS[runtimeEnvironment] ?? PLAN_IDS.production;

        if (planType === 'free') {
            return { id: environment.free, price: 0 };
        }

        if (planType === 'skills') {
            return period === 'monthly'
                ? { id: environment.skills_monthly, price: 15 }
                : { id: environment.skills_yearly, price: 99 };
        }

        if (planType === 'pro') {
            return period === 'monthly'
                ? { id: environment.pro_monthly, price: 39 }
                : { id: environment.pro_yearly, price: 299 };
        }

        if (planType === 'pro2') {
            return { id: environment.pro2_monthly, price: 49 };
        }

        return null;
    };

    const getCampaignPlanData = (planType, period) => {
        if (!productsByType) return null;
        if (planType === 'free') {
            return Object.values(productsByType).find((product) => !!product?.free)?.free ?? null;
        }

        const product = productsByType[planType];
        if (!product) return null;

        if (period === 'monthly') return product.monthly;
        return product.yearly;
    };

    const freePlanIdForRedirect = useMemo(() => {
        if (activeCampaign && !enterpriseOnly) {
            const campaignFreePlanId = productsByType?.skills?.free?.id ?? null;
            if (campaignFreePlanId) {
                return campaignFreePlanId;
            }
            if (forceStandardTemplateForCampaign) {
                const environment = PLAN_IDS[runtimeEnvironment] ?? PLAN_IDS.production;
                return environment.free;
            }
            return null;
        }
        const environment = PLAN_IDS[runtimeEnvironment] ?? PLAN_IDS.production;
        return environment.free;
    }, [activeCampaign, enterpriseOnly, forceStandardTemplateForCampaign, productsByType, runtimeEnvironment]);

    const ctaState = useCallback((planId, defaultLabel = 'GET STARTED') => {
        if (user && currentPlanId && Number(currentPlanId) === Number(planId)) {
            return { label: 'Current', disabled: true };
        }
        if (user) {
            return { label: 'UPGRADE', disabled: false };
        }
        return { label: defaultLabel, disabled: false };
    }, [currentPlanId, user]);

    const shouldDisplayPaidPlan = useCallback((planPrice, planId = null) => {
        if (allowDowngradesInCampaign) return true;
        if (!hasPaidSubscription) return true;
        if (currentPlanId && planId !== null && Number(currentPlanId) === Number(planId)) return true;

        const parsedPrice = Number(planPrice);
        if (!Number.isFinite(parsedPrice)) return true;
        return parsedPrice >= currentSubscriptionPriceSafe;
    }, [allowDowngradesInCampaign, currentPlanId, currentSubscriptionPriceSafe, hasPaidSubscription]);

    const getCardOverride = useCallback((productKey, plan, fallbackPlanName = null, fallbackPeriodicityType = null) => {
        if (!productKey || !plan) return null;

        const normalizedPlan = {
            ...plan,
            name: plan.name ?? fallbackPlanName,
            periodicity_type: plan.periodicity_type ?? fallbackPeriodicityType,
        };

        return resolvePlanCardOverride({
            campaign: resolvedCampaignKey,
            productKey,
            plan: normalizedPlan,
            environment: runtimeEnvironment,
        });
    }, [resolvedCampaignKey, runtimeEnvironment]);

    const campaignCards = useMemo(() => {
        if ((!activeCampaign && !paymentLinkId) || enterpriseOnly || !productsByType) {
            return [];
        }

        const allProducts = Object.values(productsByType);

        return allProducts
            .map((product) => {
                const selectedPlan = billingPeriod === 'monthly' ? product?.monthly : product?.yearly;
                if (!selectedPlan) {
                    return null;
                }
                if (!shouldDisplayPaidPlan(Number(selectedPlan.price), selectedPlan.id)) {
                    return null;
                }

                const productName = String(product?.name ?? '').toLowerCase();
                const isEnterpriseProduct = productName.includes('enterprise');
                const isSkillsProduct = productName.includes('sample') || productName.includes('skills');
                const isProProduct = productName.includes('pro') || product?.metadata?.accent === true;
                const overrideProductKey = resolveOverrideProductKey(product?.name);

                const baseTitle = isEnterpriseProduct ? 'Teams' : (product?.metadata?.name ?? product?.name ?? 'Plan');
                const baseSubtitle = isEnterpriseProduct ? 'Agencies, Studios and Businesses' : (isSkillsProduct ? 'For beginners and students' : 'For Editors and Artists');
                const fallbackDownloads = isEnterpriseProduct ? '100+ Pro' : (isSkillsProduct ? '20 Basic' : '50 Pro');

                const cta = isEnterpriseProduct
                    ? { label: user ? 'UPGRADE' : 'LEARN MORE', disabled: false }
                    : ctaState(selectedPlan.id);
                const planOverride = getCardOverride(overrideProductKey, selectedPlan, product?.name, selectedPlan?.periodicity_type);

                const baseCard = {
                    campaignClass: activeCampaign === 'black-friday' ? 'black-friday' : '',
                    name: baseTitle,
                    subtitle: baseSubtitle,
                    price: billingPeriod === 'monthly' ? Number(selectedPlan.price) : Math.ceil(Number(selectedPlan.price) / 12),
                    oldPrice: billingPeriod === 'yearly' ? campaignConfig?.oldPrice : null,
                    billingNote: activeCampaign === 'black-friday'
                        ? (billingPeriod === 'monthly' ? 'discounted price' : `Billed $${selectedPlan.price} yearly`)
                        : (billingPeriod === 'monthly' ? 'Billed monthly' : `Billed $${selectedPlan.price} yearly`),
                    downloads: inferDownloadsFromMetadata(product?.metadata, fallbackDownloads),
                    ctaLabel: cta.label,
                    ctaDisabled: cta.disabled,
                    pic: isEnterpriseProduct ? '/images/icon.team.members.dark.svg' : null,
                    features: toCampaignCardFeatures(product?.metadata?.features ?? []),
                    accent: !isEnterpriseProduct && isProProduct,
                    accentTag: null,
                };

                return {
                    key: `${productName}-${selectedPlan.id}`,
                    isEnterpriseProduct,
                    planType: isEnterpriseProduct ? 'enterprise' : productName,
                    planId: selectedPlan.id,
                    planPrice: Number(selectedPlan.price),
                    period: billingPeriod,
                    card: applyPlanCardOverride(baseCard, planOverride),
                };
            })
            .filter((item) => !!item && (!useTeamLinkCard || !item.isEnterpriseProduct))
            .sort((a, b) => {
                const orderA = a?.card?.name?.toLowerCase().includes('skills') ? 0 : (a?.card?.name?.toLowerCase().includes('pro') ? 1 : 2);
                const orderB = b?.card?.name?.toLowerCase().includes('skills') ? 0 : (b?.card?.name?.toLowerCase().includes('pro') ? 1 : 2);
                return orderA - orderB;
            });
    }, [activeCampaign, billingPeriod, campaignConfig?.oldPrice, ctaState, enterpriseOnly, getCardOverride, paymentLinkId, productsByType, shouldDisplayPaidPlan, useTeamLinkCard, user]);

    const bundleCards = useMemo(() => {
        if (enterpriseOnly) {
            return [];
        }

        const purchasedBundleIdsSet = new Set(purchasedBundleIds);
        const teamBundlesByBundleId = new Map(
            teamBundlePurchases
                .map((teamBundle) => [Number(getTeamBundleBundleId(teamBundle)), teamBundle])
                .filter(([bundleId]) => Number.isFinite(bundleId))
        );

        return (campaignBundles ?? [])
            .map((bundle) => {
                const bundleId = Number(bundle?.id);
                if (!Number.isFinite(bundleId)) {
                    return null;
                }

                const licenseTiers = getBundleLicenseTiers(bundle);
                const selectedBundleTierId = selectedBundleTierIds[bundleId];
                const selectedBundleTier = licenseTiers.find((tier) => tier.id === selectedBundleTierId) ?? licenseTiers[0] ?? null;
                const parsedPrice = Number(selectedBundleTier?.price ?? bundle?.price);
                const bundlePrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;
                const teamBundle = teamBundlesByBundleId.get(bundleId) ?? null;
                const isPurchased = Boolean(bundle?.is_purchased) || purchasedBundleIdsSet.has(bundleId) || !!teamBundle;
                const currentMembers = toNonNegativeNumber(teamBundle?.licensed_members);
                const currentTier = licenseTiers.find((tier) => Number(tier.id) === Number(getTeamBundleCurrentTierId(teamBundle)))
                    ?? licenseTiers.find((tier) => tier.member_count === currentMembers)
                    ?? null;
                const currentTierPrice = toNonNegativeNumber(
                    teamBundle?.license_list_price ?? teamBundle?.price ?? currentTier?.price ?? bundle?.price
                );
                const isTierUpgrade = !!teamBundle && !!selectedBundleTier && selectedBundleTier.member_count > currentMembers;
                const dueToday = Math.max(0, bundlePrice - currentTierPrice);
                const teamBundleId = getTeamBundleId(teamBundle);
                const isUpgradeLoading = !!upgradingTeamBundleIds[teamBundleId];
                const ctaLabel = isTierUpgrade
                    ? (isUpgradeLoading ? 'UPGRADING…' : 'UPGRADE')
                    : (isPurchased ? 'OWNED' : 'CHECKOUT');
                const bundlePluginNames = toBundlePluginNames(bundle?.apps);
                const bundleFeatures = toBundleCardFeatures({
                    description: bundle?.description,
                    apps: bundle?.apps,
                });
                const allPluginsFeatures = isAllPluginsBundle
                    ? [
                        ...bundleFeatures.slice(0, ALL_PLUGINS_VISIBLE_FEATURES),
                        { label: '+ more', tooltip: bundlePluginNames.join('\n') },
                        { label: '+ All plugins coming in 2027', emphasized: true, marginBottom: true },
                    ]
                    : bundleFeatures;
                const resolvedBundleFeatures = isPluginsOnlySlugCampaign
                    ? [{ label: 'Full Access To:', isHeader: true }, ...allPluginsFeatures]
                    : allPluginsFeatures;
                const bundleCardImageUrl = toBundleCardImageUrl(bundle);
                const bundleRedirectUrl = toBundleRedirectUrl(bundle);

                return {
                    key: `bundle-${bundleId}`,
                    planType: 'bundle',
                    period: 'lifetime',
                    planId: bundleId,
                    planPrice: bundlePrice,
                    bundleLicenseTierId: selectedBundleTier?.id ?? null,
                    teamBundleId,
                    isTierUpgrade,
                    redirectUrl: bundleRedirectUrl,
                    card: {
                        name: String(bundle?.name ?? '').trim() || 'Bundle',
                        subtitle: BUNDLE_LIFETIME_NOTE,
                        subtitleMarginClass: 'm-b-12',
                        price: bundlePrice,
                        priceWithSmallDecimals: isPluginsOnlySlugCampaign,
                        pricePeriod: null,
                        priceBlockMarginClass: 'm-b-12',
                        priceRightTextLines: ['buy', 'once'],
                        ctaLabel,
                        ctaLeadingIcon: isPurchased ? null : 'cart',
                        ctaLeadingIconClassName: isPluginsOnlySlugCampaign ? 'bundle-offset' : '',
                        ctaDisabled: (isPurchased && !isTierUpgrade) || isUpgradeLoading,
                        showDownloadsRow: false,
                        cardClass: `bundle-card ${isAllPluginsBundle ? 'all-plugins-bundle-card' : ''}`,
                        features: resolvedBundleFeatures,
                        bundleImageUrl: bundleCardImageUrl,
                        bundleMetadataRows: isAllPluginsBundle ? [] : [
                            { label: 'Plugins', value: bundlePluginNames.join(', ') || '-' },
                            { label: 'License Type', value: selectedBundleTier ? getTierLicenseLabel(selectedBundleTier.member_count) : 'Single User License' },
                            { label: 'Duration', value: 'Lifetime' },
                        ],
                        licenseTiers,
                        selectedBundleLicenseTierId: selectedBundleTier?.id ?? null,
                        bundleTierSummary: teamBundle ? {
                            currentLicenseLabel: currentTier ? getTierLicenseLabel(currentTier.member_count) : getTierLicenseLabel(currentMembers),
                            currentTierPrice: USD_PRICE_FORMATTER.format(currentTierPrice),
                            selectedTierPrice: USD_PRICE_FORMATTER.format(bundlePrice),
                            dueToday: USD_PRICE_FORMATTER.format(dueToday),
                            isUpgrade: isTierUpgrade,
                            error: bundleUpgradeErrors[teamBundleId] ?? null,
                        } : null,
                        onBundleLicenseTierChange: (tierId) => {
                            setSelectedBundleTierIds((currentTierIds) => ({
                                ...currentTierIds,
                                [bundleId]: tierId,
                            }));
                        },
                    },
                };
            })
            .filter((bundleCard) => !!bundleCard);
    }, [bundleUpgradeErrors, campaignBundles, enterpriseOnly, isAllPluginsBundle, isPluginsOnlySlugCampaign, purchasedBundleIds, selectedBundleTierIds, teamBundlePurchases, upgradingTeamBundleIds]);

    const showCampaignEmptyState = !!activeCampaign
        && !enterpriseOnly
        && !forceStandardTemplateForCampaign
        && !isLoadingPlans
        && !isLoadingBundles
        && !isAuthenticating
        && campaignCards.length === 0
        && bundleCards.length === 0;
    const showStandardFallbackCards = showCampaignEmptyState;
    const hasFetchedProductsByType = Boolean(productsByType && Object.keys(productsByType).length > 0);
    const shouldUseCampaignPlans = Boolean(activeCampaign && !enterpriseOnly && !showStandardFallbackCards && hasFetchedProductsByType);
    const shouldUsePaymentLinkPlans = Boolean(paymentLinkId && !activeCampaign && !enterpriseOnly && hasFetchedProductsByType);
    const shouldUseApiPlans = shouldUseCampaignPlans || shouldUsePaymentLinkPlans;
    const showStandardCards = (!activeCampaign && !shouldUsePaymentLinkPlans) || showStandardFallbackCards || forceStandardTemplateForCampaign;

    const apiPeriodAvailability = useMemo(() => {
        if (enterpriseOnly || !productsByType) {
            return { hasMonthly: true, hasYearly: true };
        }

        const products = Object.values(productsByType);
        if (products.length === 0) {
            return { hasMonthly: false, hasYearly: false };
        }
        const hasMonthly = products.some((product) => !!product?.monthly);
        const hasYearly = products.some((product) => !!product?.yearly);
        return { hasMonthly, hasYearly };
    }, [enterpriseOnly, productsByType]);

    const defaultPeriodAvailability = (() => {
        if (enterpriseOnly) {
            return { hasMonthly: true, hasYearly: true };
        }

        const skillsMonthly = getDefaultPlanData('skills', 'monthly');
        const skillsYearly = getDefaultPlanData('skills', 'yearly');
        const proMonthly = !useLegacyProPricing ? getDefaultPlanData('pro', 'monthly') : null;
        const proOldMonthly = useLegacyProPricing ? getDefaultPlanData('pro2', 'monthly') : null;
        const proYearly = getDefaultPlanData('pro', 'yearly');

        const hasMonthly = (
            (skillsMonthly && shouldDisplayPaidPlan(Number(skillsMonthly.price), skillsMonthly.id)) ||
            (proMonthly && shouldDisplayPaidPlan(Number(proMonthly.price), proMonthly.id)) ||
            (proOldMonthly && shouldDisplayPaidPlan(Number(proOldMonthly.price), proOldMonthly.id))
        );

        const hasYearly = (
            (skillsYearly && shouldDisplayPaidPlan(Number(skillsYearly.price), skillsYearly.id)) ||
            (proYearly && shouldDisplayPaidPlan(Number(proYearly.price), proYearly.id))
        );

        return { hasMonthly: !!hasMonthly, hasYearly: !!hasYearly };
    })();

    const enterprisePeriodAvailability = useMemo(() => {
        if (!enterpriseOnly) {
            return { hasMonthly: true, hasYearly: true };
        }

        const visibleMonthlyPlans = enterprisePlans
            .filter((plan) => normalizePeriod(plan.periodicity_type) === 'monthly')
            .filter((plan) => shouldDisplayPaidPlan(Number(plan.price), plan.id));

        const visibleYearlyPlans = enterprisePlans
            .filter((plan) => normalizePeriod(plan.periodicity_type) === 'yearly')
            .filter((plan) => shouldDisplayPaidPlan(Number(plan.price), plan.id));

        return {
            hasMonthly: visibleMonthlyPlans.length > 0,
            hasYearly: visibleYearlyPlans.length > 0,
        };
    }, [enterpriseOnly, enterprisePlans, shouldDisplayPaidPlan]);

    const effectiveLockedBillingPeriod = allowDowngradesInCampaign ? null : lockedBillingPeriod;

    const periodAvailability = useMemo(() => {
        if (effectiveLockedBillingPeriod) {
            return {
                hasMonthly: effectiveLockedBillingPeriod === 'monthly',
                hasYearly: effectiveLockedBillingPeriod === 'yearly',
            };
        }
        if (shouldUseApiPlans) {
            return apiPeriodAvailability;
        }
        if (enterpriseOnly) {
            return enterprisePeriodAvailability;
        }
        return defaultPeriodAvailability;
    }, [apiPeriodAvailability, defaultPeriodAvailability, effectiveLockedBillingPeriod, enterpriseOnly, enterprisePeriodAvailability, shouldUseApiPlans]);

    useEffect(() => {
        if (isLoadingPlans) {
            return;
        }

        if (periodAvailability.hasMonthly && !periodAvailability.hasYearly && billingPeriod !== 'monthly') {
            setBillingPeriod('monthly');
            return;
        }

        if (!periodAvailability.hasMonthly && periodAvailability.hasYearly && billingPeriod !== 'yearly') {
            setBillingPeriod('yearly');
        }
    }, [billingPeriod, isLoadingPlans, periodAvailability.hasMonthly, periodAvailability.hasYearly]);

    const baseShowBillingToggle = useMemo(() => {
        return periodAvailability.hasMonthly && periodAvailability.hasYearly;
    }, [periodAvailability.hasMonthly, periodAvailability.hasYearly]);

    const isBillingPeriodExperimentEligible = Boolean(
        featureFlags.billingPeriodVisibilityExperiment
        && experimentSurface
        && !hasPaidSubscription
        && !hasBillingPeriodExperimentRouteExclusion
        && baseShowBillingToggle
        && !isLoadingPlans
        && !isAuthenticating
    );
    const isBillingPeriodExperimentTreatment = isBillingPeriodExperimentEligible
        && billingPeriodExperiment?.cohort === 'B';
    const showBillingToggle = baseShowBillingToggle
        && !(isBillingPeriodExperimentEligible && (!billingPeriodExperiment || isBillingPeriodExperimentTreatment));

    useEffect(() => {
        if (!featureFlags.billingPeriodVisibilityExperiment || !hasPaidSubscription) {
            return;
        }

        const persistedContext = getBillingPeriodExperimentContext();
        if (!persistedContext || billingPeriodExperimentExclusionRef.current === persistedContext.assignment_id) {
            return;
        }

        billingPeriodExperimentExclusionRef.current = persistedContext.assignment_id;
        Analytics.sendCustomEvent('billing_period_experiment_excluded', {
            reason: 'paid_subscription',
            assignment_id: persistedContext.assignment_id,
        });
        clearBillingPeriodExperiment();
        setBillingPeriodExperiment(null);
    }, [hasPaidSubscription]);

    useEffect(() => {
        if (!isBillingPeriodExperimentEligible) {
            return;
        }

        const assignment = resolveBillingPeriodExperiment({
            userSettings: user?.settings,
            surface: experimentSurface,
        });
        if (!assignment) {
            return;
        }

        setBillingPeriodExperiment((currentAssignment) => (
            currentAssignment?.assignmentId === assignment.assignmentId
            && currentAssignment?.cohort === assignment.cohort
                ? currentAssignment
                : assignment
        ));

        if (billingPeriodExperimentExposureRef.current !== assignment.assignmentId) {
            billingPeriodExperimentExposureRef.current = assignment.assignmentId;
            Analytics.sendCustomEvent('billing_period_experiment_exposure', {
                assignment_id: assignment.assignmentId,
                cohort: assignment.cohort,
                surface: assignment.surface,
            });
        }

        const settings = getBillingPeriodExperimentSettings(assignment);
        const hasMatchingSettings = settings && Object.entries(settings).every(([key, value]) => user?.settings?.[key] === value);
        if (!user?.id || !settings || hasMatchingSettings || billingPeriodExperimentSettingsSyncRef.current === assignment.assignmentId) {
            return;
        }

        billingPeriodExperimentSettingsSyncRef.current = assignment.assignmentId;
        meFn(settings)
            .then((response) => {
                if (!response || typeof setUser !== 'function') {
                    return;
                }
                setUser((currentUser) => {
                    if (!currentUser || currentUser.id !== user.id) {
                        return currentUser;
                    }
                    return {
                        ...currentUser,
                        settings: {
                            ...(currentUser.settings ?? {}),
                            ...settings,
                        },
                    };
                });
            })
            .catch(() => {
                console.log('billing period experiment settings update failed');
            });
    }, [experimentSurface, isBillingPeriodExperimentEligible, setUser, user?.id, user?.settings]);

    useEffect(() => {
        if (query.get('freq')) {
            return;
        }
        if (hasUserSelectedBillingRef.current) {
            return;
        }
        if (!showBillingToggle) {
            return;
        }

        const cohortDefault = isPluginsOnlySlugCampaign ? 'monthly' : 'yearly';
        setBillingPeriod((currentValue) => (currentValue === cohortDefault ? currentValue : cohortDefault));
    }, [isPluginsOnlySlugCampaign, query, showBillingToggle]);

    const handleBillingPeriodChange = useCallback((nextPeriod) => {
        hasUserSelectedBillingRef.current = true;
        setBillingPeriod(nextPeriod);
    }, []);

    const handleCampaignLoginRedirect = useCallback(() => {
        setCookie('crateLoginRedirect', `${window.location.pathname}${window.location.search}`, 1);
        document.location.href = '/login';
    }, []);

    const getPlanAddonsForUpsell = useCallback(async (planId) => {
        const cacheKey = String(planId);
        if (Object.prototype.hasOwnProperty.call(addonUpsellCacheRef.current, cacheKey)) {
            return addonUpsellCacheRef.current[cacheKey];
        }

        const rawAddons = await getPlanAddonsFn(planId);
        const availableAddons = Array.isArray(rawAddons)
            ? rawAddons.filter((addon) => {
                if (!addon || addon.is_active === false) {
                    return false;
                }
                return Number.isFinite(getAddonPriceId(addon));
            })
            : [];
        addonUpsellCacheRef.current[cacheKey] = availableAddons;
        return availableAddons;
    }, []);

    useEffect(() => {
        if (!enterpriseOnly || !enterprisePlans.length) {
            setEnterprisePlanAddonsById({});
            return;
        }

        const uniquePlanIds = [...new Set(
            enterprisePlans
                .map((plan) => String(plan?.id ?? '').trim())
                .filter((planId) => !!planId)
        )];

        if (!uniquePlanIds.length) {
            setEnterprisePlanAddonsById({});
            return;
        }

        let isCancelled = false;

        const cachedAddonsByPlan = {};
        const missingPlanIds = [];

        uniquePlanIds.forEach((planId) => {
            if (Object.prototype.hasOwnProperty.call(addonUpsellCacheRef.current, planId)) {
                cachedAddonsByPlan[planId] = addonUpsellCacheRef.current[planId];
                return;
            }
            missingPlanIds.push(planId);
        });

        setEnterprisePlanAddonsById((previousValue) => {
            const nextValue = {};
            uniquePlanIds.forEach((planId) => {
                if (Object.prototype.hasOwnProperty.call(cachedAddonsByPlan, planId)) {
                    nextValue[planId] = cachedAddonsByPlan[planId];
                    return;
                }
                if (Object.prototype.hasOwnProperty.call(previousValue, planId)) {
                    nextValue[planId] = previousValue[planId];
                }
            });
            return nextValue;
        });

        if (!missingPlanIds.length) {
            return;
        }

        Promise.all(
            missingPlanIds.map(async (planId) => {
                try {
                    const addons = await getPlanAddonsForUpsell(planId);
                    return { planId, addons };
                } catch (error) {
                    console.log('Error while preloading plan addons', planId, error);
                    return { planId, addons: null };
                }
            })
        ).then((results) => {
            if (isCancelled) {
                return;
            }

            const fetchedAddonsByPlan = results.reduce((accumulator, result) => {
                if (Array.isArray(result?.addons)) {
                    accumulator[result.planId] = result.addons;
                }
                return accumulator;
            }, {});

            if (Object.keys(fetchedAddonsByPlan).length === 0) {
                return;
            }

            setEnterprisePlanAddonsById((previousValue) => ({
                ...previousValue,
                ...fetchedAddonsByPlan,
            }));
        });

        return () => {
            isCancelled = true;
        };
    }, [enterpriseOnly, enterprisePlans, getPlanAddonsForUpsell]);

    const closeAddonUpsell = useCallback(() => {
        setIsAddonUpsellVisible(false);
        setAddonUpsellSelectedIds([]);
        setAddonUpsellOptions([]);
        setAddonUpsellContext(null);
        setAddonUpsellError(null);
    }, []);

    const navigateToSignup = useCallback((planId, campaignCode = activeCampaign, { addonPriceIds = [], providerOverride = null, checkoutItemType = null, bundleLicenseTierId = null, redirectUrl = null } = {}) => {
        const queryParams = new URLSearchParams();
        if (campaignCode) {
            queryParams.set('c', campaignCode);
        }
        if (paymentLinkId) {
            queryParams.set('payment_link_id', paymentLinkId);
        }
        if (checkoutItemType) {
            queryParams.set('checkout_item_type', checkoutItemType);
        }
        if (Number.isFinite(Number(bundleLicenseTierId)) && Number(bundleLicenseTierId) > 0) {
            queryParams.set('bundle_license_tier_id', String(Number(bundleLicenseTierId)));
        }
        if (typeof redirectUrl === 'string' && redirectUrl.trim()) {
            queryParams.set('redirect_url', redirectUrl.trim());
        }
        if (Array.isArray(addonPriceIds) && addonPriceIds.length > 0) {
            queryParams.set('addon_price_ids', addonPriceIds.join(','));
        }
        if (nativeApp) {
            queryParams.set('native_app', nativeApp);
        }
        const queryString = queryParams.toString();
        const querySuffix = queryString ? `?${queryString}` : '';
        const resolvedProvider = providerOverride || paymentProvider;
        setTimeout(() => {
            document.location.href = `/signup/${planId}/${resolvedProvider}${querySuffix}`;
        }, 300);
    }, [activeCampaign, nativeApp, paymentLinkId, paymentProvider]);

    const shouldSkipAddonUpsellForPaypalUpgrade = useCallback(({ planPrice }) => {
        if (!enterpriseOnly) {
            return false;
        }

        const subscription = user?.team?.subscription;
        if (!subscription?.paypal) {
            return false;
        }

        const currentPrice = Number(subscription?.plan?.price ?? 0);
        const nextPrice = Number(planPrice ?? 0);
        if (!Number.isFinite(currentPrice) || !Number.isFinite(nextPrice)) {
            return false;
        }

        return nextPrice > currentPrice;
    }, [enterpriseOnly, user?.team?.subscription]);

    const openAddonUpsellForPlan = useCallback(({ planId, campaignCode, availableAddons }) => {
        if (!Array.isArray(availableAddons) || availableAddons.length === 0) {
            navigateToSignup(planId, campaignCode);
            return;
        }

        setAddonUpsellContext({
            planId,
            campaignCode,
        });
        setAddonUpsellSelectedIds([]);
        setAddonUpsellOptions(availableAddons);
        setIsAddonUpsellVisible(true);
    }, [navigateToSignup]);

    const handleAddonUpsellToggle = useCallback((addonId) => {
        setAddonUpsellSelectedIds((currentIds) => {
            if (currentIds.includes(addonId)) {
                return currentIds.filter((id) => id !== addonId);
            }
            return [...currentIds, addonId];
        });
    }, []);

    const handleAddonUpsellContinue = useCallback((useSelectedAddons) => {
        if (!addonUpsellContext?.planId) {
            closeAddonUpsell();
            return;
        }

        const selectedAddonIds = useSelectedAddons ? addonUpsellSelectedIds : [];
        const shouldForceStripeProvider = paymentProvider === 'p' && selectedAddonIds.length > 0;
        const providerOverride = shouldForceStripeProvider ? 's' : null;

        closeAddonUpsell();
        navigateToSignup(addonUpsellContext.planId, addonUpsellContext.campaignCode, {
            addonPriceIds: selectedAddonIds,
            providerOverride,
        });
    }, [addonUpsellContext, addonUpsellSelectedIds, closeAddonUpsell, navigateToSignup, paymentProvider]);

    const handlePlanClick = useCallback(async ({ planType, period, planId, planPrice, campaignCode, bundleLicenseTierId = null, teamBundleId = null, isTierUpgrade = false, redirectUrl = null }) => {
        if (planType === 'enterprise') {
            try {
                Analytics.click('plans_enterprise_cta');
            } catch (error) {
                console.log('analytics error', error);
            }
            setTimeout(() => {
                document.location.href = '/teams';
            }, 300);
            return;
        }

        try {
            Analytics.addToCart(planPrice ?? 0, `${planType}_${period}`);
            const experimentContext = getBillingPeriodExperimentContext();
            if (experimentContext) {
                Analytics.sendCustomEvent('billing_period_experiment_plan_selected', {
                    assignment_id: experimentContext.assignment_id,
                    plan_id: planId ?? null,
                    plan_period: period,
                    plan_type: planType,
                });
            }
        } catch (error) {
            console.log('analytics error', error);
        }

        if (!planId) {
            return;
        }

        if (planType === 'bundle' && isTierUpgrade && teamBundleId !== null && bundleLicenseTierId !== null) {
            setUpgradingTeamBundleIds((currentIds) => ({...currentIds, [teamBundleId]: true}));
            setBundleUpgradeErrors((currentErrors) => ({...currentErrors, [teamBundleId]: null}));
            try {
                const result = await teamFn.appLicenses.checkoutUpgrade(teamBundleId, bundleLicenseTierId, 'stripe');
                const checkoutUrl = getCheckoutUrl(result);
                if (checkoutUrl) {
                    window.location.assign(checkoutUrl);
                    return;
                }
                if (typeof reloadCurrentUser === 'function') {
                    await reloadCurrentUser();
                }
                setBundlePurchaseRefreshKey((currentKey) => currentKey + 1);
            } catch (error) {
                setBundleUpgradeErrors((currentErrors) => ({
                    ...currentErrors,
                    [teamBundleId]: error?.response?.data?.message || 'Unable to start the license upgrade checkout.',
                }));
            } finally {
                setUpgradingTeamBundleIds((currentIds) => {
                    const nextIds = {...currentIds};
                    delete nextIds[teamBundleId];
                    return nextIds;
                });
            }
            return;
        }

        if (!enterpriseOnly) {
            navigateToSignup(planId, campaignCode, {
                checkoutItemType: planType === 'bundle' ? 'bundle' : null,
                bundleLicenseTierId: planType === 'bundle' ? bundleLicenseTierId : null,
                redirectUrl: planType === 'bundle' ? redirectUrl : null,
            });
            return;
        }

        if (isAddonUpsellLoading) {
            return;
        }

        if (shouldSkipAddonUpsellForPaypalUpgrade({ planPrice })) {
            navigateToSignup(planId, campaignCode);
            return;
        }

        const cachedPlanIdKey = String(planId);
        if (Object.prototype.hasOwnProperty.call(enterprisePlanAddonsById, cachedPlanIdKey)) {
            openAddonUpsellForPlan({
                planId,
                campaignCode,
                availableAddons: enterprisePlanAddonsById[cachedPlanIdKey],
            });
            return;
        }

        setIsAddonUpsellLoading(true);
        setAddonUpsellError(null);
        try {
            const availableAddons = await getPlanAddonsForUpsell(planId);
            setEnterprisePlanAddonsById((previousValue) => ({
                ...previousValue,
                [cachedPlanIdKey]: availableAddons,
            }));
            openAddonUpsellForPlan({
                planId,
                campaignCode,
                availableAddons,
            });
        } catch (error) {
            console.log('Error while loading plan addons', error);
            setAddonUpsellError('Unable to load add-ons for this plan right now.');
            navigateToSignup(planId, campaignCode);
        } finally {
            setIsAddonUpsellLoading(false);
        }

    }, [enterpriseOnly, enterprisePlanAddonsById, getPlanAddonsForUpsell, isAddonUpsellLoading, navigateToSignup, openAddonUpsellForPlan, reloadCurrentUser, shouldSkipAddonUpsellForPaypalUpgrade]);

    useEffect(() => {
        if (!isAddonUpsellVisible) {
            return;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeAddonUpsell();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [closeAddonUpsell, isAddonUpsellVisible]);

    useEffect(() => {
        if (!enterpriseOnly) {
            closeAddonUpsell();
        }
    }, [closeAddonUpsell, enterpriseOnly]);

    useEffect(() => {
        if (addonUpsellSelectedIds.length === 0 || addonUpsellOptions.length === 0) {
            return;
        }

        const availableAddonPriceIds = new Set(addonUpsellOptions.map((addon) => getAddonPriceId(addon)).filter((id) => Number.isFinite(id)));
        const hasInvalidSelection = addonUpsellSelectedIds.some((addonId) => !availableAddonPriceIds.has(addonId));
        if (hasInvalidSelection) {
            setAddonUpsellSelectedIds((currentIds) => currentIds.filter((addonId) => availableAddonPriceIds.has(addonId)));
        }
    }, [addonUpsellOptions, addonUpsellSelectedIds]);

    useEffect(() => {
        if (freeRedirectTriggeredRef.current || !forceFree || isAuthenticating || user) {
            return;
        }

        if (isLoadingPlans) {
            return;
        }

        if (!freePlanIdForRedirect) {
            return;
        }

        freeRedirectTriggeredRef.current = true;
        try {
            Analytics.addToCart(0, 'free_monthly');
        } catch (error) {
            console.log('analytics error', error);
        }
        const queryParams = new URLSearchParams();
        if (activeCampaign) {
            queryParams.set('c', activeCampaign);
        }
        if (paymentLinkId) {
            queryParams.set('payment_link_id', paymentLinkId);
        }
        if (nativeApp) {
            queryParams.set('native_app', nativeApp);
        }
        const queryString = queryParams.toString();
        const querySuffix = queryString ? `?${queryString}` : '';
        setTimeout(() => {
            document.location.href = `/signup/${freePlanIdForRedirect}/${paymentProvider}${querySuffix}`;
        }, 300);
    }, [activeCampaign, forceFree, freePlanIdForRedirect, isAuthenticating, isLoadingPlans, nativeApp, paymentLinkId, paymentProvider, user]);

    const pageTitle = enterpriseOnly
        ? 'Team Plans'
        : (campaignConfig?.title ?? (upgrade || user ? 'Upgrade your Plan' : 'Choose your Plan'));

    const getDisplayedPlanData = shouldUseApiPlans ? getCampaignPlanData : getDefaultPlanData;
    const getStandardPlanData = useRegisterStylePricingForCampaign ? getDefaultPlanData : getDisplayedPlanData;

    const skillsPlan = getStandardPlanData('skills', billingPeriod);
    const skillsYearlyPlan = getStandardPlanData('skills', 'yearly');
    const proMonthlyPlan = !useLegacyProPricing ? getStandardPlanData('pro', 'monthly') : null;
    const proOldMonthlyPlan = useLegacyProPricing ? getStandardPlanData('pro2', 'monthly') : null;
    const proYearlyPlan = getStandardPlanData('pro', 'yearly');
    const monthlyProPlan = proMonthlyPlan ?? proOldMonthlyPlan;
    const yearlySavings = monthlyProPlan && proYearlyPlan
        ? Math.max(0, Math.round(Number(monthlyProPlan.price) * 12 - Number(proYearlyPlan.price)))
        : 0;
    const freePlan = getStandardPlanData('free', 'monthly');
    const claimFreePlan = freePlan?.id
        ? freePlan
        : (forceStandardTemplateForCampaign ? getDefaultPlanData('free', 'monthly') : null);

    const enterpriseBillingPeriod = billingPeriod === 'monthly' ? 'month' : 'year';
    const currentPeriodicityType = billingPeriod === 'monthly' ? 'month' : 'year';
    const pluginsOnlyPlan = forceStandardTemplateForCampaign
        ? getDisplayedPlanData(PLUGINS_PLAN_PRODUCT_KEY, billingPeriod)
        : null;

    const primaryPlan = forceStandardTemplateForCampaign
        ? (pluginsOnlyPlan ?? skillsPlan)
        : skillsPlan;
    useEffect(() => {
        if (ga4PricingTrackedRef.current || isAuthenticating || isLoadingPlans || isLoadingBundles || forceFree || (user && user.analytics?.enabled === false)) return;
        if (isBillingPeriodExperimentEligible && !billingPeriodExperiment) return;
        if (billingPeriod === 'yearly' && !periodAvailability.hasYearly && periodAvailability.hasMonthly) return;
        if (billingPeriod === 'monthly' && !periodAvailability.hasMonthly && periodAvailability.hasYearly) return;
        if (!enterpriseOnly && (activeCampaign || paymentLinkId) && !productsByType) return;
        let offers;
        if (enterpriseOnly) {
            offers = enterprisePlans.filter((plan) => normalizePeriod(plan.periodicity_type) === billingPeriod && shouldDisplayPaidPlan(Number(plan.price), plan.id))
                .map((plan) => ({ id: plan.id, name: 'Enterprise', price: Number(plan.price), variant: billingPeriod === 'yearly' ? 'year' : 'month' }));
        } else if (shouldUseApiPlans && !showStandardFallbackCards && !forceStandardTemplateForCampaign) {
            offers = campaignCards.map((card) => ({ id: card.planId, name: card.card.name, price: card.planPrice, variant: billingPeriod === 'yearly' ? 'year' : 'month' }));
        } else {
            offers = [
                { plan: primaryPlan, name: pluginsOnlyPlan ? 'Plugins Plan' : 'Skills' },
                { plan: billingPeriod === 'yearly' ? proYearlyPlan : (proMonthlyPlan ?? proOldMonthlyPlan), name: 'PRO' },
            ].filter(({ plan }) => plan?.id && shouldDisplayPaidPlan(Number(plan.price), plan.id))
                .map(({ plan, name }) => ({ id: plan.id, name, price: Number(plan.price), variant: billingPeriod === 'yearly' ? 'year' : 'month' }));
        }
        offers.push(...bundleCards.map((card) => ({ id: `bundle_${card.planId}`, name: card.card.name, price: card.planPrice, variant: 'lifetime' })));
        offers = offers.filter((offer, index) => Number.isFinite(offer.price) && offers.findIndex((candidate) => candidate.id === offer.id) === index);
        if (!offers.length) return;
        const createdAt = user?.created_at ? new Date(user.created_at).getTime() : NaN;
        const sent = sendGa4Event('view_pricing', {
            default_interval: billingPeriod === 'yearly' ? 'year' : 'month',
            logged_in: Boolean(user),
            ...(Number.isFinite(createdAt) ? { account_age_days: Math.max(0, Math.floor((Date.now() - createdAt) / 86400000)) } : {}),
            currency: 'USD',
            items: offers.map((offer) => ({ item_id: String(offer.id), item_name: offer.name, item_variant: offer.variant, price: offer.price, quantity: 1 })),
        });
        if (sent) ga4PricingTrackedRef.current = true;
    }, [activeCampaign, billingPeriod, billingPeriodExperiment, bundleCards, campaignCards, enterpriseOnly, enterprisePlans, forceFree, forceStandardTemplateForCampaign, isAuthenticating, isBillingPeriodExperimentEligible, isLoadingBundles, isLoadingPlans, paymentLinkId, periodAvailability, pluginsOnlyPlan, primaryPlan, proMonthlyPlan, proOldMonthlyPlan, proYearlyPlan, productsByType, shouldDisplayPaidPlan, shouldUseApiPlans, showStandardFallbackCards, user]);

    const primaryPlanType = forceStandardTemplateForCampaign
        ? (pluginsOnlyPlan ? PLUGINS_PLAN_PRODUCT_KEY : 'skills')
        : 'skills';
    const primaryPlanOverrideKey = primaryPlanType;

    const primaryPlanCta = primaryPlan ? ctaState(primaryPlan.id) : null;
    const proMonthlyCta = proMonthlyPlan ? ctaState(proMonthlyPlan.id) : null;
    const proOldMonthlyCta = proOldMonthlyPlan ? ctaState(proOldMonthlyPlan.id) : null;
    const proYearlyCta = proYearlyPlan ? ctaState(proYearlyPlan.id) : null;

    const primaryCardOverride = primaryPlan
        ? getCardOverride(primaryPlanOverrideKey, primaryPlan, primaryPlanOverrideKey, currentPeriodicityType)
        : null;
    const proMonthlyCardOverride = proMonthlyPlan
        ? getCardOverride('pro', proMonthlyPlan, 'pro', 'month')
        : null;
    const proOldMonthlyCardOverride = proOldMonthlyPlan
        ? getCardOverride('pro', proOldMonthlyPlan, 'pro', 'month')
        : null;
    const proYearlyCardOverride = proYearlyPlan
        ? getCardOverride('pro', proYearlyPlan, 'pro', 'year')
        : null;
    const enterpriseCardOverride = getCardOverride('enterprise', { periodicity_type: currentPeriodicityType }, 'enterprise', currentPeriodicityType);
    const pluginsOnlyHighlightMode = config.pluginsOnlyHighlightMode;
    const pluginsOnlyHighlightVariant = useMemo(() => {
        if (pluginsOnlyHighlightMode === 'plugins') return 'plugins';
        if (pluginsOnlyHighlightMode === 'pro') return 'pro';
        if (pluginsOnlyHighlightMode === 'off') return 'off';
        return 'plugins';
    }, [pluginsOnlyHighlightMode]);
    const isPluginsOnlyAccentPluginsVariant = pluginsOnlyHighlightVariant === 'plugins';
    const isPluginsOnlyAccentProVariant = pluginsOnlyHighlightVariant === 'pro';
    const isPluginsOnlyHighlightOffVariant = pluginsOnlyHighlightVariant === 'off';
    const pluginsOnlyProDownloadsLabel = '50 PRO';
    const pluginsOnlyEnterpriseDownloadsLabel = '100+ PRO';
    const getPluginsOnlyProFeatures = (downloadsLabel) => ([
        { label: 'Full Access To:', isHeader: true },
        { label: 'After Effects plugins', marginBottom: true },
        { label: 'Premiere Pro plugins', marginBottom: true },
        { isDivider: true },
        { html: `<b>${downloadsLabel}</b> Downloads Per Day`, noIcon: true, marginBottom12: true },
        { label: 'Full Access To:', isHeader: true },
        { label: 'VFX', marginBottom: true },
        { label: 'SFX', marginBottom: true },
        { label: 'Graphics', marginBottom: true },
        { label: '3D Models' },
        { isDivider: true },
        { label: 'Training Courses', marginBottom: true },
        { label: 'Access to Course Assets' },
    ]);
    const pluginsOnlyEnterpriseFeatures = [
        { label: 'Full Access To:', isHeader: true },
        { label: 'After Effects plugins', marginBottom: true },
        { label: 'Premiere Pro plugins', marginBottom: true },
        { isDivider: true },
        { html: `<b>${pluginsOnlyEnterpriseDownloadsLabel}</b> Downloads Per Day`, noIcon: true, marginBottom12: true },
        { label: 'Full Access To:', isHeader: true },
        { label: 'VFX', marginBottom: true },
        { label: 'SFX', marginBottom: true },
        { label: 'Graphics', marginBottom: true },
        { label: '3D Models' },
        { isDivider: true },
        { label: '5 Team Members', marginBottom: true },
        { label: 'Extended License', marginBottom: true },
        { label: 'Server Storage Permissions' },
    ];
    const pluginsOnlyPrimaryFeatures = [
        { label: 'Full Access To:', isHeader: true },
        { label: 'Crates Glass', marginBottom: true },
        { label: 'CTR Factory', marginBottom: true },
        { label: 'Easy Glow', marginBottom: true },
        { label: 'VHS Master', marginBottom: true },
        { label: 'Retro Film Essentials', marginBottom: true },
        { label: 'Doodle Outline', marginBottom: true },
        { label: 'Glow Edges', marginBottom: true },
        { label: '+ more' },
        { isDivider: true },
        { html: '<b>+ 5 Basic</b> Stock Media Per Day*', noIcon: true, marginBottom12: true },
    ];

    const isPluginsOnlyPrimary = primaryPlanType === PLUGINS_PLAN_PRODUCT_KEY;
    const effectivePrimaryCardOverride = (isPluginsOnlySlugCampaign && isPluginsOnlyPrimary) ? null : primaryCardOverride;
    const primaryCardData = primaryPlan ? applyPlanCardOverride({
        name: isPluginsOnlyPrimary ? 'Plugins Only' : 'Skills',
        subtitle: isPluginsOnlyPrimary ? 'Access all of our plugins' : 'For beginners and students',
        price: billingPeriod === 'monthly' ? Number(primaryPlan.price) : Math.ceil(Number(primaryPlan.price) / 12),
        billingNote: billingPeriod === 'monthly'
            ? (!isPluginsOnlyPrimary ? getMonthlyBillingNote(primaryPlan, skillsYearlyPlan) : null)
            : `Billed $${primaryPlan.price} yearly`,
        showSavingsTagSlot: true,
        savingsTagSlotClassName: billingPeriod === 'monthly' && !isPluginsOnlySlugCampaign && !isPluginsOnlyPrimary && getMonthlyBillingNote(primaryPlan, skillsYearlyPlan) ? 'monthly' : '',
        downloads: isPluginsOnlyPrimary ? null : '20 Basic',
        downloadsSubline: isPluginsOnlySlugCampaign || isPluginsOnlyPrimary ? null : { label: '1 Individual User' },
        ctaLabel: isPluginsOnlySlugCampaign ? 'BUY NOW' : primaryPlanCta?.label,
        ctaDisabled: primaryPlanCta?.disabled,
        showDownloadsRow: isPluginsOnlySlugCampaign ? false : !isPluginsOnlyPrimary,
        features: (isPluginsOnlySlugCampaign && isPluginsOnlyPrimary) ? pluginsOnlyPrimaryFeatures : (isPluginsOnlyPrimary ? [] : [
            { label: 'Access Basic Assets*' },
            { isDivider: true },
            { label: 'Training Courses', marginBottom: true },
            { label: 'Access to Course Assets' },
        ]),
        note: isPluginsOnlyPrimary ? null : '*Download anything that is not marked with a star',
        priceWithSmallDecimals: isPluginsOnlySlugCampaign,
        accent: isPluginsOnlySlugCampaign && isPluginsOnlyPrimary && isPluginsOnlyAccentPluginsVariant && !isPluginsOnlyHighlightOffVariant,
        accentTag: null,
    }, effectivePrimaryCardOverride) : null;

    const proMonthlyCardData = proMonthlyPlan ? applyPlanCardOverride({
        name: 'Pro',
        subtitle: 'For Editors and Artists',
        price: Number(proMonthlyPlan.price),
        billingNote: isPluginsOnlySlugCampaign ? null : getMonthlyBillingNote(proMonthlyPlan, proYearlyPlan),
        showSavingsTagSlot: true,
        savingsTagSlotClassName: billingPeriod === 'monthly' && !isPluginsOnlySlugCampaign && getMonthlyBillingNote(proMonthlyPlan, proYearlyPlan) ? 'monthly' : '',
        downloads: '50 Pro',
        downloadsSubline: isPluginsOnlySlugCampaign ? null : { label: '1 Individual User' },
        ctaLabel: isPluginsOnlySlugCampaign ? 'BUY NOW' : proMonthlyCta?.label,
        ctaDisabled: proMonthlyCta?.disabled,
        priceWithSmallDecimals: isPluginsOnlySlugCampaign,
        showDownloadsRow: isPluginsOnlySlugCampaign ? false : undefined,
        accent: isPluginsOnlySlugCampaign
            ? (isPluginsOnlyAccentProVariant && !isPluginsOnlyHighlightOffVariant)
            : true,
        accentTag: isPluginsOnlySlugCampaign ? null : 'Most Popular',
        features: isPluginsOnlySlugCampaign ? getPluginsOnlyProFeatures(pluginsOnlyProDownloadsLabel) : [
            { label: 'Full Access to:', isHeader: true },
            { label: 'Plugins + LaForge Suite', marginBottom: true },
            { label: 'VFX', marginBottom: true },
            { label: 'SFX', marginBottom: true },
            { label: 'Graphics', marginBottom: true },
            { label: '3D Models' },
            { isDivider: true },
            { label: 'Training Courses', marginBottom: true },
            { label: 'Access to Course Assets' },
        ],
    }, proMonthlyCardOverride) : null;

    const proOldMonthlyCardData = proOldMonthlyPlan ? applyPlanCardOverride({
        name: 'Pro',
        subtitle: 'For Editors and Artists',
        price: 49,
        billingNote: isPluginsOnlySlugCampaign ? null : getMonthlyBillingNote(proOldMonthlyPlan, proYearlyPlan),
        showSavingsTagSlot: true,
        savingsTagSlotClassName: billingPeriod === 'monthly' && !isPluginsOnlySlugCampaign && getMonthlyBillingNote(proOldMonthlyPlan, proYearlyPlan) ? 'monthly' : '',
        downloads: '50 Pro',
        downloadsSubline: isPluginsOnlySlugCampaign ? null : { label: '1 Individual User' },
        ctaLabel: isPluginsOnlySlugCampaign ? 'BUY NOW' : proOldMonthlyCta?.label,
        ctaDisabled: proOldMonthlyCta?.disabled,
        priceWithSmallDecimals: isPluginsOnlySlugCampaign,
        showDownloadsRow: isPluginsOnlySlugCampaign ? false : undefined,
        accent: isPluginsOnlySlugCampaign
            ? (isPluginsOnlyAccentProVariant && !isPluginsOnlyHighlightOffVariant)
            : true,
        accentTag: isPluginsOnlySlugCampaign ? null : 'Most Popular',
        features: isPluginsOnlySlugCampaign ? getPluginsOnlyProFeatures(pluginsOnlyProDownloadsLabel) : [
            { label: 'Full Access to:', isHeader: true },
            { label: 'Plugins + LaForge Suite', marginBottom: true },
            { label: 'VFX', marginBottom: true },
            { label: 'SFX', marginBottom: true },
            { label: 'Graphics', marginBottom: true },
            { label: '3D Models' },
            { isDivider: true },
            { label: 'Training Courses', marginBottom: true },
            { label: 'Access to Course Assets' },
        ],
    }, proOldMonthlyCardOverride) : null;

    const proYearlyCardData = proYearlyPlan ? applyPlanCardOverride({
        name: 'Pro - Yearly',
        accent: isPluginsOnlySlugCampaign
            ? (isPluginsOnlyAccentProVariant && !isPluginsOnlyHighlightOffVariant)
            : true,
        accentTag: isPluginsOnlySlugCampaign ? null : 'Most Popular',
        subtitle: 'For Editors and Artists',
        oldPrice: campaignConfig?.oldPrice,
        price: Math.ceil(Number(proYearlyPlan.price) / 12),
        billingNote: `Billed $${proYearlyPlan.price} yearly`,
        showSavingsTagSlot: true,
        savingsTag: yearlySavings > 0 ? `You save $${yearlySavings} vs. Monthly` : null,
        downloads: '50 Pro',
        downloadsSubline: isPluginsOnlySlugCampaign ? null : { label: '1 Individual User' },
        ctaLabel: isPluginsOnlySlugCampaign ? 'BUY NOW' : proYearlyCta?.label,
        ctaDisabled: proYearlyCta?.disabled,
        priceWithSmallDecimals: isPluginsOnlySlugCampaign,
        showDownloadsRow: isPluginsOnlySlugCampaign ? false : undefined,
        features: isPluginsOnlySlugCampaign ? getPluginsOnlyProFeatures(pluginsOnlyProDownloadsLabel) : [
            { label: 'Full Access to:', isHeader: true },
            { label: 'Plugins + LaForge Suite', marginBottom: true },
            { label: 'VFX', marginBottom: true },
            { label: 'SFX', marginBottom: true },
            { label: 'Graphics', marginBottom: true },
            { label: '3D Models' },
            { isDivider: true },
            { label: 'Training Courses', marginBottom: true },
            { label: 'Access to Course Assets' },
        ],
    }, proYearlyCardOverride) : null;
    const isPrimarySameAsProMonthly = !!(primaryPlan && proMonthlyPlan && Number(primaryPlan.id) === Number(proMonthlyPlan.id));
    const isPrimarySameAsProOldMonthly = !!(primaryPlan && proOldMonthlyPlan && Number(primaryPlan.id) === Number(proOldMonthlyPlan.id));
    const isPrimarySameAsProYearly = !!(primaryPlan && proYearlyPlan && Number(primaryPlan.id) === Number(proYearlyPlan.id));

    const enterpriseCardData = applyPlanCardOverride({
        name: 'Teams',
        subtitle: 'Agencies, Studios and Businesses',
        pic: '/images/icon.team.members.dark.svg',
        downloads: '100+ Pro',
        downloadsSubline: isPluginsOnlySlugCampaign ? null : {
            label: '5+ Team Members',
        },
        ctaLabel: isPluginsOnlySlugCampaign ? 'SEE OPTIONS' : (user ? 'UPGRADE' : 'LEARN MORE'),
        priceWithSmallDecimals: isPluginsOnlySlugCampaign,
        showDownloadsRow: isPluginsOnlySlugCampaign ? false : undefined,
        accent: false,
        accentTag: null,
        showSavingsTagSlot: true,
        features: isPluginsOnlySlugCampaign ? pluginsOnlyEnterpriseFeatures : [
            { label: 'Full Access to:', isHeader: true },
            { label: 'Plugins + LaForge Suite', marginBottom: true },
            { label: 'VFX', marginBottom: true },
            { label: 'SFX', marginBottom: true },
            { label: 'Graphics', marginBottom: true },
            { label: '3D Models' },
            { isDivider: true },
            { label: 'Training Courses', marginBottom: true },
            { label: 'Access to Course Assets' },
            { isDivider: true },
            { label: 'Extended License', marginBottom: true },
            { label: 'Server Storage Permissions' },
        ],
    }, enterpriseCardOverride);
    const activeProPlan = billingPeriod === 'monthly'
        ? (proMonthlyPlan ?? proOldMonthlyPlan)
        : proYearlyPlan;
    const activeProCardData = billingPeriod === 'monthly'
        ? (proMonthlyCardData ?? proOldMonthlyCardData)
        : proYearlyCardData;
    const isPrimarySameAsActivePro = billingPeriod === 'monthly'
        ? (isPrimarySameAsProMonthly || isPrimarySameAsProOldMonthly)
        : isPrimarySameAsProYearly;
    const activeProPlanType = billingPeriod === 'monthly' && !proMonthlyPlan && !!proOldMonthlyPlan ? 'pro2' : 'pro';
    const activeProPlanPrice = billingPeriod === 'monthly' && !proMonthlyPlan && !!proOldMonthlyPlan
        ? 49
        : Number(activeProPlan?.price ?? 0);
    const activeProCampaignCode = billingPeriod === 'monthly' && !proMonthlyPlan && !!proOldMonthlyPlan
        ? 'MeganpZQLY'
        : (forceStandardTemplateForCampaign ? null : undefined);
    const pluginsOnlySubscriptionOrder = isPluginsOnlyAccentPluginsVariant
        ? ['pro', 'plugins', 'enterprise']
        : ['plugins', 'pro', 'enterprise'];
    const addonUpsellSelectedCount = addonUpsellSelectedIds.length;
    const addonUpsellPrimaryCtaLabel = addonUpsellSelectedCount > 0
        ? `Continue with ${addonUpsellSelectedCount} add-on${addonUpsellSelectedCount > 1 ? 's' : ''}`
        : 'Continue';
    const hasBundleCards = bundleCards.length > 0;
    const visiblePluginsOnlySubscriptionCardCount = isPluginsOnlySlugCampaign && showStandardCards
        ? pluginsOnlySubscriptionOrder.reduce((count, cardType) => {
            if (cardType === 'pro') {
                return count + Number(
                    !!activeProPlan
                    && !!activeProCardData
                    && !isPrimarySameAsActivePro
                    && shouldDisplayPaidPlan(Number(activeProPlan.price), activeProPlan.id)
                );
            }

            if (cardType === 'plugins') {
                return count + Number(
                    !!primaryPlan
                    && !!primaryCardData
                    && shouldDisplayPaidPlan(primaryPlan.price, primaryPlan.id)
                );
            }

            return count + 1;
        }, 0)
        : 0;
    const visibleSubscriptionCardCount = (
        (shouldUseApiPlans && !showStandardFallbackCards && !forceStandardTemplateForCampaign ? campaignCards.length : 0)
        + Number(shouldUseApiPlans && !showStandardFallbackCards && !forceStandardTemplateForCampaign && useTeamLinkCard)
        + visiblePluginsOnlySubscriptionCardCount
        + Number(
            !isPluginsOnlySlugCampaign
            && showStandardCards
            && !!primaryPlan
            && !!primaryCardData
            && shouldDisplayPaidPlan(primaryPlan.price, primaryPlan.id)
        )
        + Number(
            !isPluginsOnlySlugCampaign
            && showStandardCards
            && billingPeriod === 'monthly'
            && !isPrimarySameAsProMonthly
            && !!proMonthlyPlan
            && !!proMonthlyCardData
            && shouldDisplayPaidPlan(Number(proMonthlyPlan.price), proMonthlyPlan.id)
        )
        + Number(
            !isPluginsOnlySlugCampaign
            && showStandardCards
            && billingPeriod === 'monthly'
            && !isPrimarySameAsProOldMonthly
            && !!proOldMonthlyPlan
            && !!proOldMonthlyCardData
            && shouldDisplayPaidPlan(49, proOldMonthlyPlan.id)
        )
        + Number(
            !isPluginsOnlySlugCampaign
            && showStandardCards
            && !!proYearlyPlan
            && !!proYearlyCardData
            && !isPrimarySameAsProYearly
            && billingPeriod === 'yearly'
            && shouldDisplayPaidPlan(Number(proYearlyPlan.price), proYearlyPlan.id)
        )
        + Number(!isPluginsOnlySlugCampaign && showStandardCards)
    );
    const visibleEnterpriseCardCount = enterpriseOnly
        ? enterprisePlans
            .filter((plan) => normalizePeriod(plan.periodicity_type) === billingPeriod)
            .filter((plan) => shouldDisplayPaidPlan(Number(plan.price), plan.id))
            .length
        : 0;
    const shouldRenderCompactPlansLayout = shouldUseCompactPlansLayout({
        viewportWidth,
        subscriptionCardCount: enterpriseOnly ? visibleEnterpriseCardCount : visibleSubscriptionCardCount,
        bundleCardCount: enterpriseOnly ? 0 : bundleCards.length,
    });
    const subscriptionCardsContent = (
        <>
            {shouldUseApiPlans && !showStandardFallbackCards && !forceStandardTemplateForCampaign && campaignCards.map((campaignCard) => (
                <Card
                    key={campaignCard.key}
                    plan={campaignCard.card}
                    onClick={() => handlePlanClick({
                        planType: campaignCard.planType,
                        period: campaignCard.period,
                        planId: campaignCard.planId,
                        planPrice: campaignCard.planPrice,
                    })}
                />
            ))}

            {shouldUseApiPlans && !showStandardFallbackCards && !forceStandardTemplateForCampaign && useTeamLinkCard && (
                <Card
                    key="campaign-teams-link"
                    plan={enterpriseCardData}
                    onClick={() => handlePlanClick({
                        planType: 'enterprise',
                        period: billingPeriod,
                    })}
                />
            )}

            {isPluginsOnlySlugCampaign && showStandardCards && pluginsOnlySubscriptionOrder.map((cardType) => {
                if (cardType === 'pro') {
                    if (!activeProPlan || !activeProCardData || isPrimarySameAsActivePro || !shouldDisplayPaidPlan(Number(activeProPlan.price), activeProPlan.id)) {
                        return null;
                    }
                    return (
                        <Card
                            key={`plugins-only-pro-${billingPeriod}`}
                            plan={activeProCardData}
                            onClick={() => handlePlanClick({
                                planType: activeProPlanType,
                                period: billingPeriod,
                                planId: activeProPlan.id,
                                planPrice: activeProPlanPrice,
                                campaignCode: activeProCampaignCode,
                            })}
                        />
                    );
                }
                if (cardType === 'plugins') {
                    if (!primaryPlan || !primaryCardData || !shouldDisplayPaidPlan(primaryPlan.price, primaryPlan.id)) {
                        return null;
                    }
                    return (
                        <Card
                            key={`plugins-only-primary-${billingPeriod}`}
                            plan={primaryCardData}
                            onClick={() => handlePlanClick({
                                planType: primaryPlanType,
                                period: billingPeriod,
                                planId: primaryPlan.id,
                                planPrice: Number(primaryPlan.price),
                                campaignCode: forceStandardTemplateForCampaign
                                    ? (isPluginsOnlyPrimary ? activeCampaign : null)
                                    : undefined,
                            })}
                        />
                    );
                }
                return (
                    <Card
                        key={`plugins-only-enterprise-${billingPeriod}`}
                        plan={enterpriseCardData}
                        onClick={() => handlePlanClick({
                            planType: 'enterprise',
                            period: billingPeriod,
                            campaignCode: forceStandardTemplateForCampaign ? null : undefined,
                        })}
                    />
                );
            })}

            {!isPluginsOnlySlugCampaign && showStandardCards && primaryPlan && primaryCardData && shouldDisplayPaidPlan(primaryPlan.price, primaryPlan.id) && (
                <Card
                    plan={primaryCardData}
                    onBillingNoteClick={() => handleBillingPeriodChange('yearly')}
                    onClick={() => handlePlanClick({
                        planType: primaryPlanType,
                        period: billingPeriod,
                        planId: primaryPlan.id,
                        planPrice: Number(primaryPlan.price),
                        campaignCode: forceStandardTemplateForCampaign
                            ? (isPluginsOnlyPrimary ? activeCampaign : null)
                            : undefined,
                    })}
                />
            )}

            {!isPluginsOnlySlugCampaign && showStandardCards && billingPeriod === 'monthly' && !isPrimarySameAsProMonthly && proMonthlyPlan && proMonthlyCardData && shouldDisplayPaidPlan(Number(proMonthlyPlan.price), proMonthlyPlan.id) && (
                <Card
                    plan={proMonthlyCardData}
                    onBillingNoteClick={() => handleBillingPeriodChange('yearly')}
                    onClick={() => handlePlanClick({
                        planType: 'pro',
                        period: 'monthly',
                        planId: proMonthlyPlan.id,
                        planPrice: Number(proMonthlyPlan.price),
                        campaignCode: forceStandardTemplateForCampaign ? null : undefined,
                    })}
                />
            )}

            {!isPluginsOnlySlugCampaign && showStandardCards && billingPeriod === 'monthly' && !isPrimarySameAsProOldMonthly && proOldMonthlyPlan && proOldMonthlyCardData && shouldDisplayPaidPlan(49, proOldMonthlyPlan.id) && (
                <Card
                    plan={proOldMonthlyCardData}
                    onBillingNoteClick={() => handleBillingPeriodChange('yearly')}
                    onClick={() => handlePlanClick({
                        planType: 'pro2',
                        period: 'monthly',
                        planId: proOldMonthlyPlan.id,
                        planPrice: 49,
                        campaignCode: 'MeganpZQLY',
                    })}
                />
            )}

            {!isPluginsOnlySlugCampaign && showStandardCards && proYearlyPlan && proYearlyCardData && !isPrimarySameAsProYearly && billingPeriod === 'yearly' && shouldDisplayPaidPlan(Number(proYearlyPlan.price), proYearlyPlan.id) && (
                <Card
                    plan={proYearlyCardData}
                    onClick={() => handlePlanClick({
                        planType: 'pro',
                        period: 'yearly',
                        planId: proYearlyPlan.id,
                        planPrice: Number(proYearlyPlan.price),
                        campaignCode: forceStandardTemplateForCampaign ? null : undefined,
                    })}
                />
            )}

            {!isPluginsOnlySlugCampaign && showStandardCards && <Card
                plan={enterpriseCardData}
                onClick={() => handlePlanClick({
                    planType: 'enterprise',
                    period: billingPeriod,
                    campaignCode: forceStandardTemplateForCampaign ? null : undefined,
                })}
            />}
        </>
    );

    return (
        <>
            <div className={`plans_v3__page ${activeCampaign ? 'campaign' : ''} ${enterpriseOnly ? 'enterprise_mode' : ''} ${shouldRenderCompactPlansLayout ? 'compact_layout' : ''}`}>
                {campaignConfig?.backgroundVideo && (
                    <div className="plans_v3__campaign_bg" aria-hidden="true">
                        <video autoPlay loop muted playsInline>
                            <source src={campaignConfig.backgroundVideo} type="video/mp4" />
                        </video>
                    </div>
                )}

                <div className="plans_v3__wrapper">
                    <div className="plans_v3__page__title m-b-24">{pageTitle}</div>
                    {campaignConfig?.subtitle && <div className="plans_v3__page__campaign_subtitle m-b-20">{campaignConfig.subtitle}</div>}

                    {!isAuthenticating && !isLoadingPlans && showBillingToggle && !showCampaignEmptyState && !hasBundleCards && (
                        <Tabs onChange={handleBillingPeriodChange} value={billingPeriod} yearlySavings={yearlySavings} />
                    )}

                    {(isAuthenticating || isLoadingPlans) && (
                        <div className="plans_v3__loading">
                            <Spinner />
                        </div>
                    )}

                    {!isAuthenticating && !isLoadingPlans && !enterpriseOnly && (
                        <>
                            {showCampaignEmptyState && (
                                <div className="plans_v3__empty_state">
                                    <div className="plans_v3__empty_state__title">This campaign is for selected accounts</div>
                                    <div className="plans_v3__empty_state__subtitle">
                                        {!user
                                            ? 'Log in to check your eligibility.'
                                            : 'This promotion is not available for your account.'}
                                    </div>
                                    <div className="plans_v3__empty_state__actions">
                                        {!user && (
                                            <button type="button" className="plans_v3__empty_state__action_btn" onClick={handleCampaignLoginRedirect}>
                                                Log in
                                            </button>
                                        )}
                                        <a className="plans_v3__empty_state__action_btn secondary" href="#plans-v3-standard-offers">
                                            Check out these other offers
                                        </a>
                                        {user && (
                                            <a className="plans_v3__empty_state__action_btn secondary" href="mailto:support@productioncrate.com">
                                                Contact support
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {showStandardFallbackCards && (
                                <div id="plans-v3-standard-offers" className="plans_v3__fallback_controls">
                                    <div className="plans_v3__fallback_controls__divider" aria-hidden="true" />
                                    {showBillingToggle && (
                                        <Tabs onChange={handleBillingPeriodChange} value={billingPeriod} yearlySavings={yearlySavings} />
                                    )}
                                </div>
                            )}

                            {hasBundleCards && (
                                <div className="plans_v3__plans_with_bundles">
                                    <div className="plans_v3__plans_with_bundles__column bundle_column">
                                        <div className="plans_v3__plans_section_header bundle_header">
                                            <div className="plans_v3__plans_section_title">
                                                {isAllPluginsBundle ? 'Lifetime Access' : 'Selected Plugin'}
                                            </div>
                                        </div>
                                        <div className="plans_v3__plans_list bundles_only">
                                            {bundleCards.map((bundleCard) => (
                                                <div className="plans_v3__bundle_card_wrapper" key={bundleCard.key}>
                                                    <Card
                                                        plan={bundleCard.card}
                                                        onClick={() => handlePlanClick({
                                                            planType: bundleCard.planType,
                                                            period: bundleCard.period,
                                                            planId: bundleCard.planId,
                                                            planPrice: bundleCard.planPrice,
                                                            bundleLicenseTierId: bundleCard.bundleLicenseTierId,
                                                            teamBundleId: bundleCard.teamBundleId,
                                                            isTierUpgrade: bundleCard.isTierUpgrade,
                                                            redirectUrl: bundleCard.redirectUrl,
                                                        })}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="plans_v3__plans_with_bundles__column subscriptions_column">
                                        <div className="plans_v3__plans_section_header">
                                            <div className="plans_v3__plans_section_title">or Subscribe and Save</div>
                                            {showBillingToggle && (
                                                <div className="plans_v3__plans_section_header__toggle">
                                                    <Tabs onChange={handleBillingPeriodChange} value={billingPeriod} yearlySavings={yearlySavings} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="plans_v3__plans_list subscriptions_only">
                                            {subscriptionCardsContent}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!hasBundleCards && (
                                <div className="plans_v3__plans_list">
                                    {subscriptionCardsContent}
                                </div>
                            )}
                        </>
                    )}

                    {!isAuthenticating && !isLoadingPlans && enterpriseOnly && (
                        <div className="plans_v3__plans_list enterprise_cards">
                            {enterprisePlans
                                .filter((plan) => normalizePeriod(plan.periodicity_type) === billingPeriod)
                                .filter((plan) => shouldDisplayPaidPlan(Number(plan.price), plan.id))
                                .map((plan) => {
                                    const monthlyPlan = enterprisePlans.find((candidate) => (
                                        candidate.teamSize === plan.teamSize
                                        && normalizePeriod(candidate.periodicity_type) === 'monthly'
                                    ));
                                    const features = (plan.metadata?.features ?? [])
                                        .filter((feature) => !isTeamMembersFeature(feature))
                                        .map((feature) => {
                                            const cleanLabel = toFeatureLabel(feature);
                                            return {
                                                label: cleanLabel,
                                                html: feature?.variable ? null : String(feature?.name ?? ''),
                                            };
                                        })
                                        .filter((feature) => !!feature.label);
                                    return (
                                        <Card
                                            key={`enterprise-plan-${plan.id}`}
                                            plan={{
                                                name: getTeamPlanDisplayName(plan.teamSize),
                                                subtitle: TEAM_PLAN_SUBTITLE,
                                                subtitleMarginClass: 'm-b-24',
                                                oldPrice: billingPeriod === 'yearly' && monthlyPlan
                                                    ? `$${Number(monthlyPlan.price) * 12}/year`
                                                    : '\u00A0',
                                                oldPriceNoStrike: billingPeriod !== 'yearly',
                                                price: billingPeriod === 'yearly' ? Math.ceil(Number(plan.price) / 12) : Number(plan.price),
                                                pricePeriod: billingPeriod === 'yearly' ? '/mo' : null,
                                                billingNote: billingPeriod === 'yearly' ? `billed $${plan.price} yearly` : 'per month',
                                                downloads: getEnterpriseDownloads(plan.metadata?.features),
                                                downloadsSubline: { label: getTeamPlanMembersLabel(plan.teamSize) },
                                                ctaLabel: ctaState(plan.id).label,
                                                ctaDisabled: ctaState(plan.id).disabled,
                                                accent: false,
                                                accentTag: null,
                                                cardClass: 'enterprise-card',
                                                features,
                                            }}
                                            onClick={() => handlePlanClick({
                                                planType: `enterprise-${plan.teamSize}`,
                                                period: enterpriseBillingPeriod,
                                                planId: plan.id,
                                                planPrice: Number(plan.price),
                                                campaignCode: 'enterprise-only',
                                            })}
                                        />
                                    );
                                })}

                            <Card
                                plan={{
                                    name: 'Custom Team',
                                    subtitle: 'adaptive for your needs',
                                    subtitleMarginClass: 'enterprise-custom-team__subtitle',
                                    customPriceIcon: '/images/icon.team.members.dark.svg',
                                    priceText: 'Unique Price',
                                    ctaLabel: 'CONTACT US',
                                    accent: true,
                                    cardClass: 'enterprise-card enterprise-custom-team',
                                    features: [
                                        { label: 'All users you need', html: '<b>All users</b> you need' },
                                        { label: 'No Download limits', html: '<b>No Download</b> limits' },
                                        { label: 'Full Access to All Libraries' },
                                        { label: 'Full Access to Plugins' },
                                        { label: 'Team management' },
                                        { label: 'Extended License' },
                                    ],
                                }}
                                onClick={() => {
                                    document.location.href = 'mailto:support@productioncrate.com';
                                }}
                            />
                        </div>
                    )}

                    {!isAuthenticating && !isLoadingPlans && !hasActiveRealSubscription && (
                        <div className={`plans_v3__provider_container ${hasBundleCards ? 'bundle_layout' : ''}`}>
                            <div className="plans_v3__provider_container__label">Preferred payment provider:</div>
                            <img
                                className={`plans_v3__provider_container__logo ${paymentProvider === 's' ? 'active' : 'disabled'}`}
                                src="/images/logo.provider.stripe.svg"
                                alt="Stripe Payment"
                                style={{ padding: '2px 6px' }}
                                onClick={() => setPaymentProvider('s')}
                            />
                            <img
                                className={`plans_v3__provider_container__logo ${paymentProvider === 'p' ? 'active' : 'disabled'}`}
                                src="/images/logo.provider.paypal.svg"
                                alt="Paypal Payment"
                                style={{ width: '60px', padding: '3px 4px' }}
                                onClick={() => setPaymentProvider('p')}
                            />
                        </div>
                    )}

                    {!isAuthenticating && !isLoadingPlans && enterpriseOnly && (
                        <>
                            <div className="plans_v3__enterprise_info">
                                <div className="plans_v3__enterprise_info__title">HOW IT WORKS?</div>
                                <ul>
                                    <li>Choose the plan that covers your team size. For example, if you have 15 people, select the 20-person plan.</li>
                                    <li>You get the full download allowance of your chosen plan, even if you don't use all the spots. For example, a 15-person team would get all 400 daily downloads from the 20-person plan.</li>
                                    <li>You have the flexibility to add or remove users at any time.</li>
                                </ul>
                            </div>
                            <div className="plans_v3__enterprise_info">
                                <div className="plans_v3__enterprise_info__title">WHY THIS IS GREAT?</div>
                                <ul>
                                    <li>Simple and straightforward pricing, making it easy to scale.</li>
                                    <li>Your team can download simultaneously.</li>
                                    <li>Your allotted download will provide more flexibility for your team members.</li>
                                </ul>
                            </div>
                        </>
                    )}

                    {!enterpriseOnly && !user && !isAuthenticating && !isLoadingPlans && claimFreePlan?.id && (
                        <div className="plans_v3__page__subtitle m-t-8">
                            Looking for a free sample account?{' '}
                            <a
                                className="plans_v3__page__subtitle accent"
                                href="/register/free"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handlePlanClick({
                                        planType: 'free',
                                        period: 'monthly',
                                        planId: claimFreePlan.id,
                                        planPrice: 0,
                                        campaignCode: forceStandardTemplateForCampaign ? null : undefined,
                                    });
                                }}
                            >
                                Click here
                            </a>
                        </div>
                    )}
                </div>
            </div>

            <div className={`plans_v3__addons_upsell ${isAddonUpsellVisible ? 'visible' : ''}`} aria-hidden={!isAddonUpsellVisible}>
                <div className="plans_v3__addons_upsell__overlay" onClick={closeAddonUpsell} />
                <div className="plans_v3__addons_upsell__panel" role="dialog" aria-modal="true" aria-label="Team add-ons">
                    <button type="button" className="plans_v3__addons_upsell__close" onClick={closeAddonUpsell}>
                        Close
                    </button>
                    <div className="plans_v3__addons_upsell__title">Add optional add-ons</div>
                    <div className="plans_v3__addons_upsell__subtitle">
                        Boost your Team plan before checkout. You can also continue without add-ons.
                    </div>
                    {paymentProvider === 'p' && (
                        <div className="plans_v3__addons_upsell__notice">
                            Add-ons are billed with Stripe. If you choose any add-on, checkout provider will switch from PayPal to Stripe.
                        </div>
                    )}
                    {addonUpsellError && <div className="plans_v3__addons_upsell__error">{addonUpsellError}</div>}
                    <div className="plans_v3__addons_upsell__table_wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Add-on</th>
                                    <th>Description</th>
                                    <th>Price</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {addonUpsellOptions.map((addon) => {
                                    const addonPriceId = getAddonPriceId(addon);
                                    const isAddonPriceIdValid = Number.isFinite(addonPriceId);
                                    const isSelected = isAddonPriceIdValid ? addonUpsellSelectedIds.includes(addonPriceId) : false;
                                    const addonPeriodicityType = getAddonPeriodicityType(addon);
                                    return (
                                        <tr key={`addon-row-${addon.id}-${addonPriceId ?? 'na'}`}>
                                            <td>{addon.name}</td>
                                            <td>{addon.description || '-'}</td>
                                            <td>{`${formatAddonPrice(getAddonPriceValue(addon))}${addonPeriodicityType ? ` / ${addonPeriodicityType}` : ''}`}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`plans_v3__addons_upsell__toggle ${isSelected ? 'selected' : ''}`}
                                                    disabled={!isAddonPriceIdValid}
                                                    onClick={() => {
                                                        if (isAddonPriceIdValid) {
                                                            handleAddonUpsellToggle(addonPriceId);
                                                        }
                                                    }}
                                                >
                                                    {isSelected ? 'Selected' : 'Select'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="plans_v3__addons_upsell__actions">
                        <button type="button" className="btn-secondary" onClick={() => handleAddonUpsellContinue(false)}>
                            Continue without add-ons
                        </button>
                        <button type="button" className="btn-primary" onClick={() => handleAddonUpsellContinue(true)}>
                            {addonUpsellPrimaryCtaLabel}
                        </button>
                    </div>
                </div>
            </div>

            <div className="plans_v3__banner">
                <div className="plans_v3__banner__title">Used by the best creatives</div>
                <img className="plans_v3__banner__logos" src="/images/creatives.logos.png" alt="Used by the best creatives" />
            </div>

            <Faq donateUncheckHandle={() => {}} />
            <Footer />
        </>
    );
};

export default PlansV3;
